import { getGoogleReportRequestError } from './reportRequestError';
import ReportToolbarButton from '../../shared/ReportToolbarButton';
import { useEffect, useState, useContext, useRef, useCallback, useMemo } from 'react';
import {
    Box, Typography,
    ThemeProvider, useTheme, useMediaQuery, Chip, TextField, MenuItem,
    Switch,
    Divider,
    Tab,
    Skeleton,
    Card,
    CardContent,
    CardHeader,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import moment from 'moment';
import { AppVarsContext } from '../../../context/AppVarsContext';
import { lightenHexColor } from '../../../dwi-functions';
import { Autocomplete } from '@mui/material';
import Comments from '../../../views/comments';
import TableCard from '../../../system/table-card';
import ViMarcTableCard from '../../../system/vimarc-table-card';
import { useToken } from '../../../Login/TokenProvider';
import TrendChart from '../../../report-components/trend-chart';
import StackAdaptAnalysis from '../../../system/StackAdaptAnalysis';
import { ImMagicWand } from 'react-icons/im';
import ReactApexChart from 'react-apexcharts';
import { FaDatabase } from 'react-icons/fa6';
import ReportConversionSelect from '../../../system/controls/report-conversions';
import LocationMap from '../../../system/geo/LocationMap';
import { MdFilterAlt } from 'react-icons/md';
import ResponsiveTabs from '../../../system/responsive-tabs';
import GoogleCreativeDetails from '../CreativeDetailsTab';
import { formatUsd } from '../../../system/report-formatters';
import { applyTrendTooltip } from '../../../system/apex-tooltip';
import { useUserProfile } from '../../../context/UserProvider';
import ReportPerformanceOverview, { ReportSectionIntro } from '../../shared/ReportPerformanceOverview';
import { createReportSurfaceTheme, getReportPageSx, getReportSurfaceSx, getAnalyticsReportTabsSx, reportColors, styleReportChart } from '../../shared/report-design';
import ReportQuestionAssistant from '../../../system/controls/report-question-assistant';
import CampaignSelectionFilter, { normalizeCampaignOptions, selectionPayload } from '../../shared/campaignSelectionFilter';
import { normalizeReportProfileVisibility, resolveShowCost } from '../../../system/report-visibility';
import GoogleOutcomeVisuals from './GoogleOutcomeVisuals';


function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

function stripHtml(html) {
    if (!html) return '';
    return String(html)
        .replace(/<style[\s\S]*?<\/style>/gi, '')
        .replace(/<script[\s\S]*?<\/script>/gi, '')
        .replace(/<\/?[^>]+(>|$)/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

function normalizeAnalysisItems(items) {
    if (!Array.isArray(items)) return [];
    return items
        .map((item) => (typeof item === 'string' ? item : item?.text || ''))
        .map((item) => stripHtml(item))
        .map((item) => item.trim())
        .filter(Boolean)
        .slice(0, 6);
}

const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));
const formatCurrency = (value) => formatUsd(value);
const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;
const getConversionSelectionLabel = (value, fallback = 'Selected conversions') => {
    if (!Array.isArray(value) || !value.length) return fallback;
    const labels = value
        .map((item) => item?.ACTION || item?.name || item?.id || item)
        .map((item) => String(item || '').trim())
        .filter(Boolean);
    return labels.length ? labels.join(', ') : fallback;
};

const truncateText = (value, max = 28) => {
    const text = String(value || '');
    if (text.length <= max) return text;
    return `${text.slice(0, max - 3)}...`;
};

const safeNum = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const GOOGLE_NETWORK_LABELS = {
    SEARCH: 'Search',
    DISPLAY: 'Display',
    PERFORMANCE_MAX: 'PMax',
    DEMAND_GEN: 'YouTube'
};

const GOOGLE_NETWORK_OPTIONS = Object.entries(GOOGLE_NETWORK_LABELS).map(([type, label]) => ({
    CAMPAIGN_TYPE: type,
    LABEL: label
}));

const GOOGLE_ASSISTANT_VIEWS = [
    'Performance Overview',
    'Campaigns',
    'Ad Details',
    'Keywords',
    'Search Terms',
    'Locations',
    'Notes'
];

const ALL_SEARCH_CAMPAIGNS = 'All campaigns';

const getGoogleCampaignName = (row) => {
    const value = row?.CAMPAIGN_NAME || row?.CAMPAIGN || row?.campaign_name || row?.campaign;
    const text = String(value || '').trim();
    return text;
};

const normalizeGoogleNetworkType = (value) => {
    const raw = typeof value === 'object'
        ? (
            value?.CAMPAIGN_TYPE ||
            value?.campaign_type ||
            value?.type ||
            value?.value ||
            value?.LABEL ||
            value?.label
        )
        : value;
    const normalized = String(raw || '').trim().toUpperCase();
    if (!normalized) return null;
    if (normalized === 'PMAX' || normalized === 'PERFORMANCE MAX') return 'PERFORMANCE_MAX';
    if (normalized === 'YOUTUBE') return 'DEMAND_GEN';
    return GOOGLE_NETWORK_LABELS[normalized] ? normalized : null;
};

const normalizeGoogleNetworkSelection = (value) => {
    const items = Array.isArray(value) ? value : (value ? [value] : []);
    const seen = new Set();
    return items.reduce((output, item) => {
        const type = normalizeGoogleNetworkType(item);
        if (!type || seen.has(type)) return output;
        seen.add(type);
        output.push({
            CAMPAIGN_TYPE: type,
            LABEL: GOOGLE_NETWORK_LABELS[type]
        });
        return output;
    }, []);
};

const getGoogleReportSessionId = () => (
    sessionStorage.getItem('rid')
    || sessionStorage.getItem('cid')
    || ''
);

const getGoogleAdSetConfig = (settings, type) => {
    const key = type === 'grant' ? 'google_grant' : 'google_paid';
    return settings?.ad_sets?.[key] || {};
};

const resolveGoogleAccountId = (settings, type) => {
    const rawId = getGoogleAdSetConfig(settings, type)?.id;
    if (Array.isArray(rawId)) {
        return rawId[0] ? String(rawId[0]) : null;
    }
    if (rawId === undefined || rawId === null || rawId === '') {
        return null;
    }
    return String(rawId);
};

const resolveGoogleGmcId = (settings, reportProfile, type) => {
    const adSet = getGoogleAdSetConfig(settings, type);
    return adSet?.gmcid || settings?.gmcid_override || reportProfile?.gmcid || null;
};

const isLikelyUrl = (value) => {
    if (!value) return false;
    const text = String(value).trim();
    return /^https?:\/\//i.test(text);
};

const normalizeAssetUrls = (source) => {
    const output = [];
    const push = (val) => {
        if (val == null) return;
        if (Array.isArray(val)) {
            val.forEach((item) => push(item));
            return;
        }
        if (typeof val === 'object') {
            const url = val.URL || val.url || val.image_url || val.image || val.IMAGE_URL || val.IMAGE_ASSET_URL || val.ASSET_URL || val.asset_url;
            const nameUrl = isLikelyUrl(val.name) ? val.name : '';
            const candidate = url || nameUrl;
            if (isLikelyUrl(candidate)) output.push(String(candidate));
            return;
        }
        if (isLikelyUrl(val)) output.push(String(val).trim());
    };
    push(source);
    return output.filter(Boolean);
};

const pickTextItem = (source) => {
    if (!source) return '';
    const items = Array.isArray(source) ? source : [source];
    for (const item of items) {
        if (item == null) continue;
        if (typeof item === 'string') {
            const text = item.trim();
            if (text) return text;
            continue;
        }
        if (typeof item === 'object') {
            const text = item.text ?? item.TEXT ?? item.value ?? item.name ?? item.label;
            if (text) return String(text);
        }
    }
    return '';
};

const getWinningCopy = (row) => {
    if (!row || typeof row !== 'object') return { headline: '', description: '' };
    const headline =
        row?.RDA_LONG_HEADLINE ||
        pickTextItem(row?.HEADLINES) ||
        pickTextItem(row?.ETA_HEADLINES) ||
        pickTextItem(row?.RDA_HEADLINES) ||
        '';
    const description =
        pickTextItem(row?.DESCRIPTIONS) ||
        pickTextItem(row?.ETA_DESCRIPTIONS) ||
        pickTextItem(row?.RDA_DESCRIPTIONS) ||
        '';
    return { headline, description };
};

const buildSummary = (rows, labelKeys = []) => {
    const dataRows = Array.isArray(rows) ? rows : [];
    return dataRows.map((row) => {
        const label = labelKeys.map((key) => row?.[key]).find((val) => val) || row?.NAME || row?.name || 'Unknown';
        const impressions = safeNum(row?.IMPRESSIONS ?? row?.impressions);
        const clicks = safeNum(row?.CLICKS ?? row?.clicks);
        const conversions = safeNum(row?.CONVERSIONS ?? row?.conversions);
        const cost = safeNum(row?.COST ?? row?.cost);
        const revenue = safeNum(row?.REVENUE ?? row?.revenue);
        const ctr = safeNum(row?.CTR ?? row?.ctr ?? (impressions ? (clicks / impressions) * 100 : 0));
        const convRate = safeNum(row?.CONV_RATE ?? row?.CONVRATE ?? row?.convrate ?? (clicks ? (conversions / clicks) * 100 : 0));
        const roas = cost ? revenue / cost : 0;
        return {
            label,
            level: normalizeLocationLevel(row?.LEVEL ?? row?.level ?? row?.Level),
            impressions,
            clicks,
            conversions,
            cost,
            revenue,
            ctr,
            convRate,
            roas,
            raw: row
        };
    });
};

const ALL_LOCATION_LEVELS = 'All levels';

const normalizeLocationLevel = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw) return 'Location';
    if (raw.includes('postal') || raw.includes('zip')) return 'Postal Code';
    if (raw.includes('city')) return 'City';
    if (raw.includes('state') || raw.includes('region') || raw.includes('province')) return 'State / Region';
    if (raw.includes('country')) return 'Country';
    if (raw.includes('metro')) return 'Metro';
    if (raw.includes('county')) return 'County';
    if (raw.includes('neighborhood')) return 'Neighborhood';
    if (raw.includes('location')) return 'Location';
    return raw.replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const ensureApexOptions = (options, chartType, theme) => {
    const base = options && typeof options === 'object' ? options : {};
    const merged = styleReportChart(theme, {
        ...base,
        chart: {
            zoom: { enabled: false },
            toolbar: { show: false },
            ...(base.chart || {}),
            ...(chartType ? { type: chartType } : {})
        }
    });
    return applyTrendTooltip(theme, merged);
};



export default function GoogleCampaignGrid(props) {
    const isVimarcMode = Boolean(props.vimarcMode);
    const isRetailMode = Boolean(props.retailMode);
    const ReportTableCard = isVimarcMode ? ViMarcTableCard : TableCard;
    const { user } = useUserProfile();

    // console.log('Props', props);

    const pDate = new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric', timeZone: "UTC" }).format(new Date(sessionStorage.getItem('sDate'))) + "-" + new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric', timeZone: "UTC" }).format(new Date(sessionStorage.getItem('eDate')));

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [reportProfile, setreportProfile] = useState();
    const [value, setValue] = useState(0);
    const adNetworkData = GOOGLE_NETWORK_OPTIONS;
    const [searchData, setsearchData] = useState();
    const [searchAnalysis, setSearchAnalysis] = useState(null);
    const [campaignAnalysis, setCampaignAnalysis] = useState(null);
    const [keywordAnalysis, setKeywordAnalysis] = useState(null);
    const [keywordData, setkeywordData] = useState();
    const [campaignData, setCampaignData] = useState();
    const [creativeData, setCreativeData] = useState();
    const [locationData, setLocationData] = useState([]);
    const [trendData, setTrendData] = useState();
    const [data, setData] = useState();
    const [client, setclient] = useState(() => {
        try {
            const raw = sessionStorage.getItem('client');
            return raw ? JSON.parse(raw) : null;
        } catch (error) {
            return null;
        }
    });
    const [dataType, setDataType] = useState(() => normalizeGoogleNetworkSelection(['SEARCH', 'DEMAND_GEN']));
    const [showPrevious, setShowPrevious] = useState(false);
    const [campaignTab, setCampaignTab] = useState(0);
    const [searchTab, setSearchTab] = useState(0);
    const [keywordTab, setKeywordTab] = useState(0);
    const [searchCampaignFilter, setSearchCampaignFilter] = useState(ALL_SEARCH_CAMPAIGNS);
    const [locationLevelFilter, setLocationLevelFilter] = useState(ALL_LOCATION_LEVELS);
    const [conversionActions, setConversionActions] = useState([]);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [campaignOptions, setCampaignOptions] = useState([]);
    const [selectedCampaignIds, setSelectedCampaignIds] = useState(null);
    const [draftCampaignIds, setDraftCampaignIds] = useState([]);
    const token = useToken();
    const admintoken = parseInt(token.token.token);
    const reportRef = useRef();
    const fetchIdRef = useRef(0);
    const lastDateRangeRef = useRef('');
    const analysisTabsSx = {
        minHeight: 32,
        '& .MuiTabs-flexContainer': { gap: 0.5 }
    };
    const analysisTabSx = {
        minHeight: 32,
        minWidth: 0,
        px: 1,
        py: 0.5,
        fontSize: '0.75rem',
        '& .MuiTab-iconWrapper': {
            marginRight: 0.5,
            fontSize: 18
        }
    };



    const [cid] = useState(getGoogleReportSessionId());
    const brandTheme = useTheme();
    const theme = useMemo(() => createReportSurfaceTheme(brandTheme), [brandTheme]);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { appVars, appLoading } = useContext(AppVarsContext);
    const primaryColor = theme.palette.primary?.main || '#6aa7ff';
    const surfaceCardSx = useMemo(() => getReportSurfaceSx(theme), [theme]);
    const plainCardSx = useMemo(() => getReportSurfaceSx(theme), [theme]);
    const tabsShellSx = useMemo(() => ({ ...getReportSurfaceSx(theme), px: 1 }), [theme]);
    const tabsSx = getAnalyticsReportTabsSx();
    const filterButtonSx = { textTransform: 'none', fontWeight: 700 };
    const pageShellSx = useMemo(() => ({ ...getReportPageSx(theme), '& .google-executive-content .MuiPaper-root': getReportSurfaceSx(theme) }), [theme]);

    const searchAnalysisSummary = useMemo(() => {
        return normalizeAnalysisItems(searchAnalysis?.json?.summary);
    }, [searchAnalysis]);

    const campaignAnalysisSummary = useMemo(() => {
        return normalizeAnalysisItems(campaignAnalysis?.json?.summary);
    }, [campaignAnalysis]);

    const keywordAnalysisSummary = useMemo(() => {
        return normalizeAnalysisItems(keywordAnalysis?.json?.summary);
    }, [keywordAnalysis]);

    const includeAnalysis = useMemo(() => {
        if (!reportProfile?.settings) return false;
        try {
            const settings = typeof reportProfile.settings === 'object'
                ? reportProfile.settings
                : JSON.parse(reportProfile.settings);
            return Boolean(settings?.include_analysis);
        } catch (error) {
            return false;
        }
    }, [reportProfile]);

    const conversionAccountId = useMemo(() => {
        if (!reportProfile?.settings) return null;
        try {
            const settings = typeof reportProfile.settings === 'object'
                ? reportProfile.settings
                : JSON.parse(reportProfile.settings);
            return resolveGoogleAccountId(settings, props.type);
        } catch (error) {
            return null;
        }
    }, [reportProfile, props.type]);

    const conversionGmcId = useMemo(() => {
        if (!reportProfile?.settings) return reportProfile?.gmcid || null;
        try {
            const settings = typeof reportProfile.settings === 'object'
                ? reportProfile.settings
                : JSON.parse(reportProfile.settings);
            return resolveGoogleGmcId(settings, reportProfile, props.type);
        } catch (error) {
            return reportProfile?.gmcid || null;
        }
    }, [reportProfile, props.type]);

    const normalizeConversionSpec = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) {
            return value
                .map((entry) => {
                    if (entry == null) return null;
                    if (typeof entry === 'string' || typeof entry === 'number') return String(entry);
                    if (typeof entry === 'object') {
                        return entry.name || entry.label || entry.id || entry.resourceName || entry.value || entry.conversion || entry.action_type || null;
                    }
                    return null;
                })
                .filter(Boolean);
        }
        if (typeof value === 'object') {
            const nested =
                value?.conversions ||
                value?.conversion ||
                value?.conversion_actions ||
                value?.conversionActions ||
                value?.values ||
                [];
            return normalizeConversionSpec(nested);
        }
        return [];
    };

    const parseSettings = (raw) => {
        if (!raw) return null;
        if (typeof raw === 'object') return raw;
        if (typeof raw === 'string') {
            try {
                return JSON.parse(raw);
            } catch (error) {
                return null;
            }
        }
        return null;
    };

    const hasConversionSpecValue = (value) => {
        if (value == null) return false;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'object') return Object.keys(value).length > 0;
        if (typeof value === 'string') return value.trim().length > 0;
        if (typeof value === 'number') return Number.isFinite(value);
        return false;
    };

    const getGoogleSpecListForService = (spec, adSetKey) => {
        if (!spec) return [];
        if (Array.isArray(spec)) return normalizeConversionSpec(spec);
        if (typeof spec === 'object') {
            const hasPaidGrant = spec.paid !== undefined || spec.grant !== undefined;
            if (hasPaidGrant) {
                const key = adSetKey === 'google_grant' ? 'grant' : 'paid';
                return normalizeConversionSpec(spec[key] || []);
            }
            return normalizeConversionSpec(spec);
        }
        return [];
    };

    const buildGoogleConversionSpecPayload = (spec, selection, adSetKey, hasExplicitSelection) => {
        const payload = { paid: [], grant: [] };
        if (hasExplicitSelection) {
            const list = normalizeConversionSpec(selection);
            if (adSetKey === 'google_grant') payload.grant = list;
            else payload.paid = list;
            return payload;
        }

        if (spec && typeof spec === 'object' && (spec.paid !== undefined || spec.grant !== undefined)) {
            payload.paid = normalizeConversionSpec(spec.paid || []);
            payload.grant = normalizeConversionSpec(spec.grant || []);
            return payload;
        }

        const list = getGoogleSpecListForService(spec, adSetKey);
        if (adSetKey === 'google_grant') payload.grant = list;
        else payload.paid = list;
        return payload;
    };

    const pickGoogleConversionSpec = (settings, adSetKey) => {
        if (!settings || typeof settings !== 'object') return null;
        const candidates = [
            settings?.conversion_defaults?.[adSetKey],
            settings?.conversion_defaults?.google_paid,
            settings?.conversion_defaults?.google_grant,
            settings?.conversion_defaults?.google,
            settings?.conversion_defaults?.adwords,
            settings?.conversionActions?.google,
            settings?.conversion_actions?.google,
            settings?.conversionSpec?.google,
            settings?.conversion_spec?.google,
            settings?.conversions?.google,
            settings?.conversionActions?.adwords,
            settings?.conversion_actions?.adwords,
            settings?.conversionSpec?.adwords,
            settings?.conversion_spec?.adwords,
            settings?.conversions?.adwords,
            settings?.conversionActions,
            settings?.conversion_actions,
            settings?.conversionSpec,
            settings?.conversion_spec,
            settings?.conversions,
            settings?.ad_sets?.[adSetKey]?.conversionActions,
            settings?.ad_sets?.[adSetKey]?.conversion_actions,
            settings?.ad_sets?.[adSetKey]?.conversionSpec,
            settings?.ad_sets?.[adSetKey]?.conversion_spec,
            settings?.ad_sets?.[adSetKey]?.conversions
        ];
        return candidates.find(hasConversionSpecValue) || null;
    };

    const googleAdSetKey = props.type === 'grant' ? 'google_grant' : 'google_paid';
    const userSettings = useMemo(() => parseSettings(user?.settings), [user?.settings]);
    const clientSettings = useMemo(() => parseSettings(client?.settings), [client]);
    const reportSettings = useMemo(() => parseSettings(reportProfile?.settings), [reportProfile?.settings]);

    const userConversionSpecRaw = useMemo(() => (
        pickGoogleConversionSpec(userSettings, googleAdSetKey) ||
        pickGoogleConversionSpec(user, googleAdSetKey)
    ), [userSettings, user, googleAdSetKey]);

    const clientConversionSpecRaw = useMemo(() => (
        pickGoogleConversionSpec(clientSettings, googleAdSetKey) ||
        pickGoogleConversionSpec(client, googleAdSetKey)
    ), [clientSettings, client, googleAdSetKey]);

    const reportConversionSpecRaw = useMemo(() => (
        pickGoogleConversionSpec(reportSettings, googleAdSetKey)
    ), [reportSettings, googleAdSetKey]);

    const fallbackConversionSpecRaw = userConversionSpecRaw || clientConversionSpecRaw || reportConversionSpecRaw || null;
    const defaultConversionSpec = useMemo(
        () => getGoogleSpecListForService(fallbackConversionSpecRaw, googleAdSetKey),
        [fallbackConversionSpecRaw, googleAdSetKey]
    );

    const conversionSessionKey = useMemo(() => (
        props.type === 'grant' ? 'conversionSpec_google_grant' : 'conversionSpec_google_paid'
    ), [props.type]);

    const conversionSessionRef = useRef({ has: false, value: [] });
    const [adNetworksInitialized, setAdNetworksInitialized] = useState(false);
    const loadSessionConversions = () => {
        if (!conversionSessionKey) return { has: false, value: [] };
        const raw = sessionStorage.getItem(conversionSessionKey);
        if (raw === null) {
            conversionSessionRef.current = { has: false, value: [] };
            return conversionSessionRef.current;
        }
        try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && (parsed.paid !== undefined || parsed.grant !== undefined)) {
                const list = getGoogleSpecListForService(parsed, googleAdSetKey);
                conversionSessionRef.current = { has: true, value: list };
                return conversionSessionRef.current;
            }
            const normalized = normalizeConversionSpec(parsed);
            conversionSessionRef.current = { has: true, value: normalized };
            return conversionSessionRef.current;
        } catch (error) {
            conversionSessionRef.current = { has: true, value: [] };
            return conversionSessionRef.current;
        }
    };

    const defaultAdNetworkSelection = useMemo(() => (
        normalizeGoogleNetworkSelection(reportSettings?.ad_sets?.[googleAdSetKey]?.network_types)
    ), [reportSettings, googleAdSetKey]);

    const adNetworkSessionKey = useMemo(() => (
        props.type === 'grant' ? 'networkTypes_google_grant' : 'networkTypes_google_paid'
    ), [props.type]);

    useEffect(() => {
        if (!reportProfile) return;
        const sessionConversions = loadSessionConversions();
        if (sessionConversions.has) {
            setConversionActions(sessionConversions.value);
            return;
        }
        if (defaultConversionSpec.length > 0) {
            setConversionActions(defaultConversionSpec);
        }
    }, [reportProfile, defaultConversionSpec, conversionSessionKey]);

    useEffect(() => {
        setAdNetworksInitialized(false);
    }, [adNetworkSessionKey]);

    useEffect(() => {
        if (!reportProfile) return;
        let sessionNetworks = { has: false, value: [] };
        if (adNetworkSessionKey) {
            const raw = sessionStorage.getItem(adNetworkSessionKey);
            if (raw !== null) {
                try {
                    sessionNetworks = {
                        has: true,
                        value: normalizeGoogleNetworkSelection(JSON.parse(raw))
                    };
                } catch (error) {
                    sessionNetworks = { has: true, value: [] };
                }
            }
        }
        if (sessionNetworks.has && sessionNetworks.value.length > 0) {
            setDataType(sessionNetworks.value);
            setAdNetworksInitialized(true);
            return;
        }
        if (defaultAdNetworkSelection.length > 0) {
            setDataType(defaultAdNetworkSelection);
        }
        setAdNetworksInitialized(true);
    }, [reportProfile, defaultAdNetworkSelection, adNetworkSessionKey]);

    const effectiveReportProfile = useMemo(() => (
        reportProfile
            ? {
                ...reportProfile,
                showCost: isVimarcMode ? false : resolveShowCost(reportProfile)
            }
            : reportProfile
    ), [reportProfile, isVimarcMode]);

    const showCost = resolveShowCost(effectiveReportProfile);
    const googleAssistantConfig = useMemo(() => ({
        activeView: GOOGLE_ASSISTANT_VIEWS[value] || 'Performance Overview',
        filters: {
            selectedNetworks: Array.isArray(dataType)
                ? dataType.map((item) => item?.CAMPAIGN_TYPE || item?.LABEL || item)
                : [],
            conversions: Array.isArray(conversionActions)
                ? conversionActions.map((item) => item?.ACTION || item?.name || item?.id || item)
                : []
        }
    }), [conversionActions, dataType, value]);

    const creativeRows = useMemo(() => (Array.isArray(creativeData) ? creativeData : []), [creativeData]);

    const buildAdMetrics = useCallback((row) => {
        const impressions = safeNum(row?.IMPRESSIONS ?? row?.impressions);
        const clicks = safeNum(row?.CLICKS ?? row?.clicks);
        const conversions = safeNum(row?.CONVERSIONS ?? row?.conversions);
        const cost = safeNum(row?.COST ?? row?.cost);
        const revenue = safeNum(row?.REVENUE ?? row?.revenue);
        const ctr = impressions ? (clicks / impressions) * 100 : 0;
        const convRate = clicks ? (conversions / clicks) * 100 : 0;
        const cpc = clicks ? cost / clicks : 0;
        const roas = cost ? revenue / cost : 0;
        return {
            impressions,
            clicks,
            conversions,
            cost,
            revenue,
            ctr,
            convRate,
            cpc,
            roas
        };
    }, []);

    const keepRetailGoogleRow = useCallback((row) => {
        const impressions = safeNum(row?.IMPRESSIONS ?? row?.impressions);
        const clicks = safeNum(row?.CLICKS ?? row?.clicks);
        const conversions = safeNum(row?.CONVERSIONS ?? row?.conversions);
        const cost = safeNum(row?.COST ?? row?.cost);
        const revenue = safeNum(row?.REVENUE ?? row?.revenue);
        return impressions > 0 || clicks > 0 || conversions > 0 || cost > 0 || revenue > 0;
    }, []);

    const getAdGroupId = useCallback((row) => (
        row?.ADGROUP_ID ||
        row?.AD_GROUP_ID ||
        row?.adgroup_id ||
        row?.ad_group_id ||
        row?.adGroupId ||
        row?.ADGROUP ||
        null
    ), []);

    const adGroupSummaries = useMemo(() => {
        if (!creativeRows.length) return [];
        const groupMap = new Map();
        creativeRows.forEach((row) => {
            const fallbackId = `${row?.CAMPAIGN_ID || row?.CAMPAIGN_NAME || 'campaign'}-${row?.ADGROUP_NAME || row?.ADGROUP || row?.ADGROUP_ID || 'group'}`;
            const groupId = getAdGroupId(row) || fallbackId;
            if (!groupMap.has(groupId)) {
                groupMap.set(groupId, {
                    id: groupId,
                    name: row?.ADGROUP_NAME || row?.ADGROUP || 'Ad Group',
                    campaignName: row?.CAMPAIGN_NAME || row?.CAMPAIGN || 'Campaign',
                    rows: []
                });
            }
            groupMap.get(groupId).rows.push(row);
        });
        return Array.from(groupMap.values()).map((group) => {
            const totals = group.rows.reduce(
                (acc, row) => {
                    const metrics = buildAdMetrics(row);
                    acc.impressions += metrics.impressions;
                    acc.clicks += metrics.clicks;
                    acc.conversions += metrics.conversions;
                    acc.cost += metrics.cost;
                    acc.revenue += metrics.revenue;
                    return acc;
                },
                { impressions: 0, clicks: 0, conversions: 0, cost: 0, revenue: 0 }
            );
            const ctr = totals.impressions ? (totals.clicks / totals.impressions) * 100 : 0;
            const convRate = totals.clicks ? (totals.conversions / totals.clicks) * 100 : 0;
            const cpc = totals.clicks ? totals.cost / totals.clicks : 0;
            const roas = totals.cost ? totals.revenue / totals.cost : 0;
            return {
                ...group,
                metrics: {
                    ...totals,
                    ctr,
                    convRate,
                    cpc,
                    roas
                }
            };
        });
    }, [creativeRows, buildAdMetrics, getAdGroupId]);

    const pickWinnerFromGroup = useCallback((group, metricKey) => {
        if (!group?.rows?.length) return null;
        const scored = group.rows.map((row) => ({ row, metrics: buildAdMetrics(row) }));
        let filtered = scored;
        if (metricKey === 'ctr') {
            filtered = scored.filter((item) => item.metrics.impressions > 0 || item.metrics.clicks > 0);
        } else if (metricKey === 'convRate') {
            filtered = scored.filter((item) => item.metrics.clicks > 0);
        }
        const ranked = filtered.length ? filtered : scored;
        ranked.sort((a, b) => (b.metrics[metricKey] ?? 0) - (a.metrics[metricKey] ?? 0));
        return ranked[0] || null;
    }, [buildAdMetrics]);

    const pickTopAdByMetric = useCallback((metricKey, filterGroups) => {
        if (!adGroupSummaries.length) return null;
        const eligibleGroups = typeof filterGroups === 'function'
            ? adGroupSummaries.filter(filterGroups)
            : adGroupSummaries;
        const groupsToRank = eligibleGroups.length ? eligibleGroups : adGroupSummaries;
        const rankedGroups = groupsToRank
            .slice()
            .sort((a, b) => (b.metrics[metricKey] ?? 0) - (a.metrics[metricKey] ?? 0));
        const topGroup = rankedGroups[0];
        const winner = topGroup ? pickWinnerFromGroup(topGroup, metricKey) : null;
        if (!winner) return null;
        return { group: topGroup, row: winner.row, metrics: winner.metrics };
    }, [adGroupSummaries, pickWinnerFromGroup]);

    const topAdByClicks = useMemo(
        () => pickTopAdByMetric('clicks'),
        [pickTopAdByMetric]
    );

    const topAdByImpressions = useMemo(
        () => pickTopAdByMetric('impressions', (group) => group.metrics.impressions > 0),
        [pickTopAdByMetric]
    );

    const topAdByCtr = useMemo(
        () => pickTopAdByMetric('ctr', (group) => group.metrics.impressions > 0 || group.metrics.clicks > 0),
        [pickTopAdByMetric]
    );

    const topAdByConversions = useMemo(
        () => pickTopAdByMetric('conversions', (group) => group.metrics.conversions > 0),
        [pickTopAdByMetric]
    );

    const topAdByConvRate = useMemo(
        () => pickTopAdByMetric('convRate', (group) => group.metrics.clicks > 0),
        [pickTopAdByMetric]
    );

    const getAdLabel = useCallback((row) => {
        const { headline } = getWinningCopy(row);
        return row?.AD_NAME || row?.AD_ID || headline || row?.ADGROUP_NAME || row?.CAMPAIGN_NAME || 'Ad';
    }, []);

    const getAssetPreviewUrl = useCallback((row) => {
        const urls = normalizeAssetUrls([
            row?.IMAGE_URLS,
            row?.IMAGE_URL,
            row?.IMAGE_ASSET_URL,
            row?.ASSET_URLS,
            row?.ASSET_URL,
            row?.ASSETS,
            row?.ASSET
        ]);
        return urls[0] || '';
    }, []);

    const renderTopAdCard = (title, winner, highlight, chips = []) => (
        <Card variant="outlined">
            <CardHeader
                title={title}
                sx={{
                    py: 1.25,
                    '& .MuiCardHeader-title': { fontSize: 14, fontWeight: 700 }
                }}
            />
            <CardContent sx={{ display: 'grid', gap: 1.25, pt: 1.5, pb: 1.5 }}>
                {winner ? (
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 1.25,
                            gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 120px) 1fr' },
                            alignItems: 'start'
                        }}
                    >
                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                overflow: 'hidden',
                                minHeight: 90,
                                background: theme.palette.action.hover,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {getAssetPreviewUrl(winner.row) ? (
                                <Box
                                    component="img"
                                    src={getAssetPreviewUrl(winner.row)}
                                    alt={`Asset preview for ${getAdLabel(winner.row)}`}
                                    loading="lazy"
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            ) : (
                                <Typography variant="caption" color="text.secondary">
                                    No asset preview
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ display: 'grid', gap: 0.75 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {getAdLabel(winner.row)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {winner.group?.campaignName || winner.row.CAMPAIGN_NAME} · {winner.group?.name || winner.row.ADGROUP_NAME}
                            </Typography>
                            {(() => {
                                const copy = getWinningCopy(winner.row);
                                if (!copy.headline && !copy.description) return null;
                                return (
                                    <Box sx={{ display: 'grid', gap: 0.25 }}>
                                        {copy.headline ? (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    fontWeight: 700,
                                                    lineHeight: 1.3,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 1,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden'
                                                }}
                                                title={copy.headline}
                                            >
                                                {copy.headline}
                                            </Typography>
                                        ) : null}
                                        {copy.description ? (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    lineHeight: 1.3,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden'
                                                }}
                                                title={copy.description}
                                            >
                                                {copy.description}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                );
                            })()}
                            {highlight ? (
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {highlight.label}
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        {highlight.value}
                                    </Typography>
                                </Box>
                            ) : null}
                            {chips.length ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {chips.map((chip) => (
                                        <Chip key={chip.label} size="small" label={`${chip.label} ${chip.value}`} sx={{ height: 22 }} />
                                    ))}
                                </Box>
                            ) : null}
                        </Box>
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No ad data available.
                    </Typography>
                )}
            </CardContent>
        </Card>
    );

    const campaignSummary = useMemo(() => (
        buildSummary(campaignData, ['CAMPAIGN', 'CAMPAIGN_NAME', 'NAME'])
    ), [campaignData]);

    const keywordSummary = useMemo(() => (
        buildSummary(keywordData, ['KEYWORD', 'KEYWORD_TEXT', 'NAME'])
    ), [keywordData]);

    const searchCampaignOptions = useMemo(() => {
        const uniqueCampaigns = Array.from(
            new Set((Array.isArray(searchData) ? searchData : []).map((row) => getGoogleCampaignName(row)))
        ).sort((a, b) => a.localeCompare(b));
        return [ALL_SEARCH_CAMPAIGNS, ...uniqueCampaigns];
    }, [searchData]);

    const filteredSearchData = useMemo(() => {
        const rows = Array.isArray(searchData) ? searchData : [];
        if (searchCampaignFilter === ALL_SEARCH_CAMPAIGNS) return rows;
        return rows.filter((row) => getGoogleCampaignName(row) === searchCampaignFilter);
    }, [searchCampaignFilter, searchData]);

    const hasSearchCampaignBreakout = useMemo(() => (
        searchCampaignOptions.length > 1
    ), [searchCampaignOptions]);

    useEffect(() => {
        if (searchCampaignFilter !== ALL_SEARCH_CAMPAIGNS && !searchCampaignOptions.includes(searchCampaignFilter)) {
            setSearchCampaignFilter(ALL_SEARCH_CAMPAIGNS);
        }
    }, [searchCampaignFilter, searchCampaignOptions]);

    const searchSummary = useMemo(() => (
        buildSummary(searchData, ['SEARCH', 'SEARCH_TERM', 'QUERY', 'NAME'])
    ), [searchData]);

    const locationSummary = useMemo(() => (
        buildSummary(locationData, ['LOCATION', 'NAME', 'CITY', 'REGION'])
    ), [locationData]);

    const locationLevelOptions = useMemo(() => (
        [ALL_LOCATION_LEVELS, ...Array.from(new Set(
            locationSummary
                .map((row) => row.level)
                .filter(Boolean)
        ))]
    ), [locationSummary]);

    const filteredLocationSummary = useMemo(() => {
        if (locationLevelFilter === ALL_LOCATION_LEVELS) return locationSummary;
        return locationSummary.filter((row) => row.level === locationLevelFilter);
    }, [locationLevelFilter, locationSummary]);

    useEffect(() => {
        if (locationLevelFilter !== ALL_LOCATION_LEVELS && !locationLevelOptions.includes(locationLevelFilter)) {
            setLocationLevelFilter(ALL_LOCATION_LEVELS);
        }
    }, [locationLevelFilter, locationLevelOptions]);

    const locationTopRevenue = useMemo(() => (
        filteredLocationSummary.slice().sort((a, b) => b.revenue - a.revenue)
    ), [filteredLocationSummary]);

    const locationConversionLabel = useMemo(() => (
        getConversionSelectionLabel(conversionActions.length ? conversionActions : defaultConversionSpec, 'Selected conversion set')
    ), [conversionActions, defaultConversionSpec]);

    const bestLocationByRoas = useMemo(() => (
        filteredLocationSummary
            .slice()
            .filter((row) => row.cost > 0)
            .sort((a, b) => b.roas - a.roas)[0] || null
    ), [filteredLocationSummary]);

    const topLocationByTraffic = useMemo(() => (
        filteredLocationSummary
            .slice()
            .sort((a, b) => (b.clicks - a.clicks) || (b.impressions - a.impressions))[0] || null
    ), [filteredLocationSummary]);

    const bestLocationByCtr = useMemo(() => {
        const qualifiedRows = filteredLocationSummary.filter((row) => row.impressions >= 100);
        const comparisonPool = qualifiedRows.length ? qualifiedRows : filteredLocationSummary.filter((row) => row.impressions > 0);
        return comparisonPool
            .slice()
            .sort((a, b) => (b.ctr - a.ctr) || (b.clicks - a.clicks))[0] || null;
    }, [filteredLocationSummary]);

    const bestLocationByConvRate = useMemo(() => {
        const qualifiedRows = filteredLocationSummary.filter((row) => row.clicks >= 10);
        const comparisonPool = qualifiedRows.length ? qualifiedRows : filteredLocationSummary.filter((row) => row.clicks > 0);
        return comparisonPool
            .slice()
            .sort((a, b) => (b.convRate - a.convRate) || (b.conversions - a.conversions))[0] || null;
    }, [filteredLocationSummary]);

    const locationLevelBreakdown = useMemo(() => {
        const grouped = filteredLocationSummary.reduce((acc, row) => {
            const key = row.level || 'Location';
            if (!acc[key]) {
                acc[key] = {
                    level: key,
                    locations: 0,
                    impressions: 0,
                    clicks: 0,
                    conversions: 0,
                    revenue: 0,
                    cost: 0,
                };
            }
            acc[key].locations += 1;
            acc[key].impressions += row.impressions;
            acc[key].clicks += row.clicks;
            acc[key].conversions += row.conversions;
            acc[key].revenue += row.revenue;
            acc[key].cost += row.cost;
            return acc;
        }, {});

        return Object.values(grouped).sort((a, b) => (b.locations - a.locations) || (b.clicks - a.clicks));
    }, [filteredLocationSummary]);

    const hasLocationRevenue = useMemo(() => (
        filteredLocationSummary.some((row) => row.revenue > 0)
    ), [filteredLocationSummary]);

    const locationTrafficChartRows = useMemo(() => (
        filteredLocationSummary
            .slice()
            .filter((row) => row.clicks > 0 || row.impressions > 0)
            .sort((a, b) => (b.clicks - a.clicks) || (b.impressions - a.impressions))
            .slice(0, 8)
    ), [filteredLocationSummary]);

    const locationValueChartRows = useMemo(() => (
        filteredLocationSummary
            .slice()
            .filter((row) => (
                hasLocationRevenue
                    ? (row.revenue > 0 || row.conversions > 0)
                    : (row.conversions > 0 || row.clicks > 0)
            ))
            .sort((a, b) => (
                hasLocationRevenue
                    ? ((b.revenue - a.revenue) || (b.conversions - a.conversions))
                    : ((b.conversions - a.conversions) || (b.clicks - a.clicks))
            ))
            .slice(0, 8)
    ), [filteredLocationSummary, hasLocationRevenue]);

    const locationTrafficOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '58%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [reportColors.revenue],
        xaxis: { categories: locationTrafficChartRows.map((row) => truncateText(row.label, 26)) },
        tooltip: {
            y: { formatter: (val) => formatNumber(val) }
        }
    }), [locationTrafficChartRows]);

    const locationTrafficSeries = [
        { name: 'Clicks', data: locationTrafficChartRows.map((row) => row.clicks) }
    ];

    const locationValueOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '58%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [hasLocationRevenue ? reportColors.conversions : reportColors.categories[6]],
        xaxis: {
            categories: locationValueChartRows.map((row) => truncateText(row.label, 26)),
            labels: hasLocationRevenue ? { formatter: (val) => formatCurrency(val) } : undefined
        },
        tooltip: {
            y: {
                formatter: (val) => (
                    hasLocationRevenue
                        ? formatCurrency(val)
                        : formatNumber(val)
                )
            }
        }
    }), [hasLocationRevenue, locationValueChartRows]);

    const locationValueSeries = [
        {
            name: hasLocationRevenue ? 'Revenue' : 'Conversions',
            data: locationValueChartRows.map((row) => (hasLocationRevenue ? row.revenue : row.conversions))
        }
    ];

    const locationLevelMixOptions = useMemo(() => ({
        chart: { type: 'donut', toolbar: { show: false } },
        labels: locationLevelBreakdown.map((row) => row.level),
        colors: reportColors.categories,
        legend: { position: 'bottom' },
        dataLabels: { enabled: false },
        stroke: { width: 0 },
        tooltip: {
            y: {
                formatter: (val) => `${formatNumber(val)} locations`
            }
        }
    }), [locationLevelBreakdown]);

    const locationLevelMixSeries = locationLevelBreakdown.map((row) => row.locations);

    const campaignTopSpend = useMemo(() => (
        showCost ? campaignSummary.slice().sort((a, b) => b.cost - a.cost).slice(0, 8) : []
    ), [campaignSummary, showCost]);

    const campaignTopRoas = useMemo(() => (
        showCost ? campaignSummary.slice().filter((row) => row.cost > 0).sort((a, b) => b.roas - a.roas).slice(0, 8) : []
    ), [campaignSummary, showCost]);

    const keywordFocusRows = useMemo(() => (
        keywordSummary
            .slice()
            .filter((row) => (isVimarcMode ? (row.impressions > 0 || row.clicks > 0) : row.clicks > 0))
            .sort((a, b) => (
                isVimarcMode
                    ? (b.clicks - a.clicks) || (b.impressions - a.impressions)
                    : b.convRate - a.convRate
            ))
            .slice(0, 5)
    ), [isVimarcMode, keywordSummary]);

    const searchFocusRows = useMemo(() => (
        searchSummary
            .slice()
            .filter((row) => (isVimarcMode ? (row.impressions > 0 || row.clicks > 0) : row.clicks > 0))
            .sort((a, b) => (
                isVimarcMode
                    ? (b.clicks - a.clicks) || (b.impressions - a.impressions)
                    : b.convRate - a.convRate
            ))
            .slice(0, 5)
    ), [isVimarcMode, searchSummary]);

    const campaignSpendOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [reportColors.categories[4], reportColors.conversions],
        xaxis: { categories: campaignTopSpend.map((row) => row.label), labels: { rotate: -15 } },
        tooltip: { y: { formatter: (val) => formatCurrency(val) } }
    }), [campaignTopSpend]);

    const campaignSpendSeries = [
        { name: 'Cost', data: campaignTopSpend.map((row) => row.cost) },
        { name: 'Revenue', data: campaignTopSpend.map((row) => row.revenue) }
    ];

    const campaignRoasOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [reportColors.revenue],
        xaxis: {
            categories: campaignTopRoas.map((row) => row.label),
            labels: { formatter: (val) => `${Number(val).toFixed(2)}x` }
        },
        tooltip: { y: { formatter: (val) => `${Number(val).toFixed(2)}x` } }
    }), [campaignTopRoas]);

    const campaignRoasSeries = [{ name: 'ROAS', data: campaignTopRoas.map((row) => row.roas) }];

    const keywordFocusOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: isVimarcMode
            ? [reportColors.revenue, reportColors.conversions]
            : [reportColors.revenue, reportColors.conversions],
        xaxis: { categories: keywordFocusRows.map((row) => row.label), labels: { rotate: -60 } },
        tooltip: { y: { formatter: (val) => formatNumber(val) } }
    }), [isVimarcMode, keywordFocusRows]);

    const keywordFocusSeries = [
        ...(isVimarcMode ? [{ name: 'Impressions', data: keywordFocusRows.map((row) => row.impressions) }] : []),
        { name: 'Clicks', data: keywordFocusRows.map((row) => row.clicks) },
        ...(!isVimarcMode ? [{ name: 'Conversions', data: keywordFocusRows.map((row) => row.conversions) }] : [])
    ];

    const searchFocusOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: isVimarcMode
            ? [reportColors.revenue, reportColors.conversions]
            : [reportColors.revenue, reportColors.conversions],
        xaxis: { categories: searchFocusRows.map((row) => row.label), labels: { rotate: -60 } },
        tooltip: { y: { formatter: (val) => formatNumber(val) } }
    }), [isVimarcMode, searchFocusRows]);

    const searchFocusSeries = [
        ...(isVimarcMode ? [{ name: 'Impressions', data: searchFocusRows.map((row) => row.impressions) }] : []),
        { name: 'Clicks', data: searchFocusRows.map((row) => row.clicks) },
        ...(!isVimarcMode ? [{ name: 'Conversions', data: searchFocusRows.map((row) => row.conversions) }] : [])
    ];

    const overviewMetrics = useMemo(() => {
        if (!data || !appVars?.tooltips) return [];
        const tips = JSON.parse(appVars.tooltips);
        const items = [
            { title: 'Impressions', data: data.impressions, explain: tips.impression, format: 'number', helper: 'Delivered impressions' },
            { title: 'Clicks', data: data.clicks, explain: tips.click, format: 'number', helper: 'Traffic generated' },
            { title: 'CTR', data: data.ctr, explain: tips.ctr, format: 'percent', helper: 'Click-through rate' },
            { title: 'Conversions', data: data.conversions, explain: tips.conversion, format: 'number', helper: 'Recorded conversions' },
            { title: 'Conversion Rate', data: data.convrate, explain: tips.conv_rate, format: 'percent', helper: 'Conversions / clicks' }
        ];
        if (!isVimarcMode) {
            items.unshift({ title: 'Revenue', data: data.revenue, explain: tips.revenue, format: 'currency', helper: 'Attributed revenue' });
            items.splice(3, 0, { title: 'Avg Transaction', data: data.avgGift, explain: tips.avg_gift, format: 'currency', helper: 'Average order value' });
        }
        if (showCost) {
            items.push({ title: 'CPC', data: data.cpc, explain: tips.cpc, format: 'currency', helper: 'Cost per click' });
        }
        return items;
    }, [appVars?.tooltips, data, isVimarcMode, showCost]);

    const overviewHeroMetrics = useMemo(() => (
        overviewMetrics.map((metric) => ({
            label: metric.title,
            value: metric.data,
            format: metric.format,
            helper: metric.helper
        }))
    ), [overviewMetrics]);

    const overviewHeroChips = useMemo(() => {
        const activeNetworks = Array.isArray(dataType)
            ? dataType.map((entry) => entry?.LABEL).filter(Boolean)
            : [];
        const topAd = isVimarcMode
            ? (topAdByImpressions || topAdByClicks || topAdByCtr)
            : (topAdByConversions || topAdByClicks || topAdByCtr);
        return [
            { label: 'Campaigns', value: campaignSummary.length },
            { label: 'Keywords', value: keywordSummary.length },
            { label: 'Search Terms', value: searchSummary.length },
            ...(activeNetworks.length ? [{ label: 'Networks', value: activeNetworks.join(', ') }] : []),
            ...(topAd?.row ? [{ label: isVimarcMode ? 'Top Ad' : 'Top Converter', value: truncateText(getAdLabel(topAd.row)) }] : [])
        ];
    }, [
        campaignSummary.length,
        dataType,
        getAdLabel,
        isVimarcMode,
        keywordSummary.length,
        searchSummary.length,
        topAdByClicks,
        topAdByConversions,
        topAdByCtr,
        topAdByImpressions
    ]);

    const topAdCards = useMemo(() => {
        const cards = [
            {
                title: 'Top Ad by Clicks',
                winner: topAdByClicks,
                highlight: topAdByClicks ? { label: 'Clicks', value: formatNumber(topAdByClicks.metrics.clicks) } : null,
                chips: topAdByClicks
                    ? [
                        { label: 'Impr', value: formatNumber(topAdByClicks.metrics.impressions) },
                        { label: 'CTR', value: formatPercent(topAdByClicks.metrics.ctr) },
                        { label: 'Conv', value: formatNumber(topAdByClicks.metrics.conversions) }
                    ]
                    : []
            },
            {
                title: 'Top Ad by CTR',
                winner: topAdByCtr,
                highlight: topAdByCtr ? { label: 'CTR', value: formatPercent(topAdByCtr.metrics.ctr) } : null,
                chips: topAdByCtr
                    ? [
                        { label: 'Impr', value: formatNumber(topAdByCtr.metrics.impressions) },
                        { label: 'Clicks', value: formatNumber(topAdByCtr.metrics.clicks) },
                        { label: 'Conv', value: formatNumber(topAdByCtr.metrics.conversions) }
                    ]
                    : []
            }
        ];

        if (isVimarcMode) {
            cards.push({
                title: 'Top Ad by Impressions',
                winner: topAdByImpressions,
                highlight: topAdByImpressions ? { label: 'Impr', value: formatNumber(topAdByImpressions.metrics.impressions) } : null,
                chips: topAdByImpressions
                    ? [
                        { label: 'Clicks', value: formatNumber(topAdByImpressions.metrics.clicks) },
                        { label: 'CTR', value: formatPercent(topAdByImpressions.metrics.ctr) },
                        { label: 'Conv', value: formatNumber(topAdByImpressions.metrics.conversions) }
                    ]
                    : []
            });
        } else {
            cards.push(
                {
                    title: 'Top Ad by Conversions',
                    winner: topAdByConversions,
                    highlight: topAdByConversions ? { label: 'Conv', value: formatNumber(topAdByConversions.metrics.conversions) } : null,
                    chips: topAdByConversions
                        ? [
                            { label: 'Clicks', value: formatNumber(topAdByConversions.metrics.clicks) },
                            { label: 'CTR', value: formatPercent(topAdByConversions.metrics.ctr) },
                            { label: 'Conv%', value: formatPercent(topAdByConversions.metrics.convRate) }
                        ]
                        : []
                },
                {
                    title: 'Top Ad by Conversion Rate',
                    winner: topAdByConvRate,
                    highlight: topAdByConvRate ? { label: 'Conv%', value: formatPercent(topAdByConvRate.metrics.convRate) } : null,
                    chips: topAdByConvRate
                        ? [
                            { label: 'Clicks', value: formatNumber(topAdByConvRate.metrics.clicks) },
                            { label: 'Conv', value: formatNumber(topAdByConvRate.metrics.conversions) },
                            { label: 'CTR', value: formatPercent(topAdByConvRate.metrics.ctr) }
                        ]
                        : []
                }
            );
        }

        return cards;
    }, [
        isVimarcMode,
        topAdByClicks,
        topAdByCtr,
        topAdByImpressions,
        topAdByConversions,
        topAdByConvRate
    ]);

    const fetchCampaigns = async (nextConversions) => {
        setLoading(true);
        setLoadError('');
        if (props.setLoading) {
            props.setLoading(true);
        }
        const fetchId = ++fetchIdRef.current;
        setSearchAnalysis(null);
        setCampaignAnalysis(null);
        setKeywordAnalysis(null);
        setCreativeData(null);
        //  props.setLoading(true);
        try {


            const settings = typeof reportProfile.settings === 'object' ? reportProfile.settings : JSON.parse(reportProfile.settings);
            const reportSessionId = getGoogleReportSessionId();
            const token = getGoogleAdSetConfig(settings, props.type)?.token || null;
            const aid = resolveGoogleAccountId(settings, props.type);
            const gmcid = resolveGoogleGmcId(settings, reportProfile, props.type);

            if (!reportSessionId || !aid) {
                throw new Error('Google report is missing a report id or Google account id.');
            }
            // console.log('Advertiser ID Google:', aid)
            const sessionConversions = loadSessionConversions();
            const hasExplicitSelection = Array.isArray(nextConversions)
                || (sessionConversions.has && sessionConversions.value.length > 0);
            let conversions = Array.isArray(nextConversions)
                ? nextConversions
                : (conversionActions.length ? conversionActions : sessionConversions.value);
            conversions = normalizeConversionSpec(conversions);
            if (!conversions.length && defaultConversionSpec.length && !hasExplicitSelection) {
                conversions = defaultConversionSpec;
            }
            const conversionSpec = buildGoogleConversionSpecPayload(
                fallbackConversionSpecRaw,
                conversions,
                googleAdSetKey,
                hasExplicitSelection
            );
            const payload = {
                cid: reportSessionId,
                aid,
                sdate: sessionStorage.getItem('sDate'),
                edate: sessionStorage.getItem('eDate'),
                type: dataType,
                token,
                gmcid,
                include_analysis: settings.include_analysis || false,
                conversionSpec,
                ...selectionPayload(selectedCampaignIds)
            };

            let requestOptions = {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            };

            const creativePayload = {
                cid: sessionStorage.getItem('rid'),
                sdate: sessionStorage.getItem('sDate'),
                edate: sessionStorage.getItem('eDate'),
                conversionSpec,
                ...selectionPayload(selectedCampaignIds)
            };

            const creativeRequestOptions = {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(creativePayload)
            };
            const campaignOptionRequestOptions = {
                ...requestOptions,
                body: JSON.stringify({
                    ...payload,
                    include_analysis: false,
                    campaignFilterActive: false,
                    campaignIds: [],
                }),
            };

            const [dataRes, trendRes, campRes, keywordRes, searchRes, creativeRes, locationRes, clientRes, campaignOptionsRes] = await Promise.all([
                fetch(`${appVars.api_url}/adwords/data`, requestOptions),
                // Client-level time series (daily/weekly) for trend charts
                fetch(`${appVars.api_url}/adwords/timeseries`, requestOptions),
                fetch(`${appVars.api_url}/adwords/campaigns`, requestOptions),
                fetch(`${appVars.api_url}/adwords/keywords`, requestOptions),
                fetch(`${appVars.api_url}/adwords/search`, requestOptions),
                fetch(`${appVars.api_url}/adwords/creatives`, creativeRequestOptions),
                fetch(`${appVars.api_url}/adwords/locations`, requestOptions),
                fetch(`${appVars.api_url}/tagmanager/${cid}`),
                fetch(`${appVars.api_url}/adwords/campaign-options`, campaignOptionRequestOptions),
            ]);

            const reportError = await getGoogleReportRequestError([dataRes, trendRes, campRes, keywordRes, searchRes, locationRes, campaignOptionsRes]);
            if (reportError) throw reportError;

            const [dataJson, trendJson, campJson, keywordJson, searchJson, creativeJson, locationJson, clientJson, campaignOptionsJson] = await Promise.all([
                dataRes.json(),
                trendRes.json(),
                campRes.json(),
                keywordRes.json(),
                searchRes.json(),
                creativeRes.json(),
                locationRes.json(),
                clientRes.json(),
                campaignOptionsRes.json(),
            ]);

            const campaignRows = Array.isArray(campJson)
                ? campJson
                : (Array.isArray(campJson?.rows) ? campJson.rows : []);
            const keywordRows = Array.isArray(keywordJson)
                ? keywordJson
                : (Array.isArray(keywordJson?.rows) ? keywordJson.rows : []);
            const searchRows = Array.isArray(searchJson)
                ? searchJson
                : (Array.isArray(searchJson?.rows) ? searchJson.rows : []);
            const creativeRows = Array.isArray(creativeJson?.rows)
                ? creativeJson.rows
                : (Array.isArray(creativeJson) ? creativeJson : []);
            const locationRows = Array.isArray(locationJson)
                ? locationJson
                : (Array.isArray(locationJson?.rows) ? locationJson.rows : []);

            if (fetchId !== fetchIdRef.current) return;

            const reportData = Array.isArray(dataJson) ? dataJson[0] : dataJson;
            setData({
                ...(reportData || {}),
                revenue: reportData?.revenue ?? reportData?.REVENUE ?? 0,
            });
            setTrendData(trendJson);
            const filteredCampaignRows = isRetailMode
                ? campaignRows.filter(keepRetailGoogleRow)
                : campaignRows;

            setCampaignData(filteredCampaignRows);
            setCampaignAnalysis(campJson?.analysis || null);
            setkeywordData(keywordRows);
            setKeywordAnalysis(keywordJson?.analysis || null);
            setsearchData(searchRows);
            setSearchAnalysis(searchJson?.analysis || null);
            setLocationData(locationRows);
            setCampaignOptions(normalizeCampaignOptions(campaignOptionsJson?.campaigns));
            const normalizedCreativeRows = creativeRows.map((row, index) => {
                const impressions = safeNum(row?.IMPRESSIONS ?? row?.impressions);
                const clicks = safeNum(row?.CLICKS ?? row?.clicks);
                const conversions = safeNum(row?.CONVERSIONS ?? row?.conversions);
                const revenue = safeNum(row?.REVENUE ?? row?.revenue);
                const cost = safeNum(row?.COST ?? row?.cost);
                const ctr = impressions ? (clicks / impressions) * 100 : 0;
                const convRate = clicks ? (conversions / clicks) * 100 : 0;
                const avgGift = conversions ? revenue / conversions : 0;
                const cpc = clicks ? cost / clicks : 0;
                const cpm = impressions ? (cost / impressions) * 1000 : 0;
                const roas = cost ? revenue / cost : 0;
                return {
                    ...row,
                    ID: row?.ID ?? row?.AD_ID ?? `${row?.CAMPAIGN_ID || 'camp'}-${row?.ADGROUP_ID || 'adgroup'}-${index}`,
                    IMPRESSIONS: impressions,
                    CLICKS: clicks,
                    CONVERSIONS: conversions,
                    REVENUE: revenue,
                    COST: cost,
                    CTR: ctr,
                    CONV_RATE: convRate,
                    avgGift,
                    CPC: cpc,
                    CPM: cpm,
                    ROAS: roas
                };
            });
            const filteredCreativeRows = isRetailMode
                ? normalizedCreativeRows.filter(keepRetailGoogleRow)
                : normalizedCreativeRows;
            filteredCreativeRows.sort((a, b) => (b.CONVERSIONS ?? 0) - (a.CONVERSIONS ?? 0));
            setCreativeData(filteredCreativeRows);
            setclient(clientJson);
            /* console.log('Campaign', campJson);
             console.log('Data', dataJson);
             console.log('Keyword', keywordJson);
             console.log('Search', searchJson);
             */





        } catch (error) {
            if (fetchId === fetchIdRef.current) {
                console.error('Error loading campaign data:', error);
                setLoadError(error.reportMessage || 'Google Ads performance could not be loaded. Check the connection or report filters, then try again.');
            }
        } finally {
            if (fetchId === fetchIdRef.current) {
                setLoading(false);
                props.setLoading(false);
            }
        }
    };




    async function fetchProfile() {
        try {
            const rp = await fetch(`${appVars.api_url}/reportprofile/${getGoogleReportSessionId()}`, {
                method: "GET",
            });
            if (!rp.ok) throw new Error('Report profile request failed');
            const response = normalizeReportProfileVisibility(await rp.json());
            setreportProfile(response);
            setLoadError('');
        } catch (error) {
            console.error('Error fetching report profile:', error);
            setLoadError('Report settings could not be loaded. Check the connection, then try again.');
        }
    }






    useEffect(() => {
        // Wait until app vars are loaded before fetching the report profile
        if (appLoading) return;
        fetchProfile();
    }, [appLoading]);


    useEffect(() => {
        if (appLoading || !reportProfile || !adNetworksInitialized) return;
        fetchCampaigns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appLoading, reportProfile, adNetworksInitialized, dataType, props.type, selectedCampaignIds]);

    useEffect(() => {
        const handleStorageChange = (event) => {
            // Refresh only when profile is ready
            const startDate = event?.detail?.sDate || sessionStorage.getItem('sDate') || '';
            const endDate = event?.detail?.eDate || sessionStorage.getItem('eDate') || '';
            const rangeKey = `${startDate}:${endDate}`;
            if (!appLoading && reportProfile && lastDateRangeRef.current !== rangeKey) {
                lastDateRangeRef.current = rangeKey;
                setTimeout(() => {
                    fetchCampaigns();
                }, 100);
            }
        };

        window.addEventListener('dc', handleStorageChange);
        return () => window.removeEventListener('dc', handleStorageChange);
    }, [appLoading, reportProfile]);


    const formatPulseMetric = (metric) => {
        const value = Number(metric.value?.count ?? metric.value ?? 0);
        return { label: metric.label, value: metric.format === 'currency' ? formatCurrency(value) : metric.format === 'percent' ? formatPercent(value) : formatNumber(value), detail: metric.helper };
    };
    const pulseMetrics = (labels) => overviewHeroMetrics.filter((metric) => labels.includes(metric.label)).map(formatPulseMetric);

    return (
        <ThemeProvider theme={theme}><Box sx={pageShellSx}>
            <Box sx={{ position: 'relative', display: 'grid', gap: 2 }}>
                <Box sx={tabsShellSx}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 1,
                            width: '100%'
                        }}
                    >
                        <ResponsiveTabs
                            value={value}
                            onChange={(event, nextValue) => setValue(nextValue)}
                            aria-label="Google report tabs"
                            sx={{ ...tabsSx, flex: '1 1 320px' }}
                        >
                            <Tab label="Performance" />
                            <Tab label="Campaigns" />
                            <Tab label="Ad Details" />
                            <Tab label="Keywords" />
                            <Tab label="Search Terms" />
                            <Tab label="Locations" />
                            <Tab label="Notes" />
                        </ResponsiveTabs>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto', mr: 1 }}>
                            <ReportToolbarButton
                                startIcon={<MdFilterAlt />}
                                onClick={() => {
                                    setDraftCampaignIds(
                                        Array.isArray(selectedCampaignIds)
                                            ? selectedCampaignIds
                                            : campaignOptions.map((option) => option.id)
                                    );
                                    setFiltersOpen(true);
                                }}
                            >
                                Filter
                            </ReportToolbarButton>
                            <Box sx={{ display: 'none', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption">Previous Period</Typography>
                                <Switch
                                    size="small"
                                    checked={showPrevious}
                                    onChange={(e) => setShowPrevious(e.target.checked)}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {Array.isArray(selectedCampaignIds) ? (
                    <Alert severity={selectedCampaignIds.length ? 'info' : 'warning'}>
                        {selectedCampaignIds.length
                            ? `${selectedCampaignIds.length} of ${campaignOptions.length} campaigns are included in every report section.`
                            : 'No campaigns are selected. Open Filter to choose campaigns.'}
                    </Alert>
                ) : null}

                {loadError ? <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => reportProfile ? fetchCampaigns() : fetchProfile()}>Try again</Button>}>{loadError}</Alert> : null}

                {value !== 0 ? <ReportSectionIntro report={props.type === 'grant' ? 'Google Grant' : 'Google Paid'} title={['Performance', 'Campaigns', 'Ad details', 'Keywords', 'Search terms', 'Locations', 'Notes'][value]} description={['', 'Compare campaign contribution and efficiency.', 'Review creative assets and their results.', 'Explore the keywords generating traffic and outcomes.', 'See which searches bring visitors to your site.', 'Review geographic reach and contribution.', 'Keep context alongside your report.'][value]} dateLabel={pDate} /> : null}
                {value === 0 ? (
                    <ReportPerformanceOverview
                        eyebrow={props.type === 'grant' ? 'Google Grant' : 'Google Paid'}
                        title={isRetailMode ? "From search to response" : "From search to action"}
                        description={isRetailMode ? "Compare retail search visibility, traffic, and recorded actions." : "See how search visibility becomes traffic and recorded outcomes."}

                        loading={loading && !data}
                        stages={[
                            { label: 'Visibility', description: 'Exposure across the selected networks.', metrics: pulseMetrics(['Impressions', 'CTR']) },
                            { label: 'Traffic', description: 'Visits and actions attributed to your ads.', metrics: pulseMetrics(['Clicks', 'Conversions']) },
                            { label: isRetailMode ? 'Response' : 'Outcomes', description: isVimarcMode ? 'Action efficiency for this period.' : 'Value and efficiency of recorded actions.', metrics: pulseMetrics(isVimarcMode ? ['Conversion Rate', ...(showCost ? ['CPC'] : [])] : ['Revenue', 'Conversion Rate']) }
                        ]}
                        supportMetrics={overviewHeroMetrics.filter((metric) => ['CPC', 'Avg Transaction'].includes(metric.label) && !isVimarcMode).map(formatPulseMetric)}
                        highlights={overviewHeroChips.map((chip) => ({ label: chip.label, title: String(chip.value), detail: 'Within the current report selection' }))}
                    />
                ) : null}

                <Dialog
                    open={filtersOpen}
                    onClose={() => setFiltersOpen(false)}
                    fullScreen={isMobile}
                    fullWidth
                    maxWidth="sm"
                    scroll="paper"
                    PaperProps={{
                        sx: {
                            ...surfaceCardSx,
                            m: { xs: 0, sm: 2 },
                            width: { xs: '100%', sm: 'auto' },
                            maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' }
                        }
                    }}
                >
                    <DialogTitle
                        sx={{
                            background: theme.palette.background.paper,
                            borderBottom: `1px solid ${alpha(primaryColor, 0.12)}`
                        }}
                    >
                        Report Filters
                    </DialogTitle>
                    <DialogContent
                        dividers
                        sx={{
                            display: 'grid',
                            gap: 2,
                            pt: 2,
                            pb: 2,
                            overflowY: 'auto',
                            maxHeight: { xs: 'calc(100vh - 180px)', sm: '70vh' }
                        }}
                    >
                        <CampaignSelectionFilter
                            options={campaignOptions}
                            selectedIds={draftCampaignIds}
                            onChange={setDraftCampaignIds}
                            loading={loading}
                        />
                        <ReportConversionSelect
                            service="google"
                            accountId={conversionAccountId}
                            gmcid={conversionGmcId}
                            value={conversionActions}
                            onChange={(next) => {
                                const normalized = normalizeConversionSpec(next);
                                setConversionActions(normalized);
                                if (conversionSessionKey) {
                                    const storedSpec = buildGoogleConversionSpecPayload(
                                        fallbackConversionSpecRaw,
                                        normalized,
                                        googleAdSetKey,
                                        true
                                    );
                                    sessionStorage.setItem(conversionSessionKey, JSON.stringify(storedSpec));
                                    conversionSessionRef.current = { has: true, value: normalized };
                                }
                                fetchCampaigns(normalized);
                            }}
                        />
                        {(data ?
                            <Autocomplete
                                options={adNetworkData}
                                value={dataType}
                                multiple={true}
                                color="primary"
                                size="small"
                                sx={({ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } })}
                                slotProps={{
                                    paper: {
                                        sx: getReportSurfaceSx(theme)
                                    }
                                }}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            size="small"
                                            key={option.CAMPAIGN_TYPE}
                                            label={option.LABEL}
                                            {...getTagProps({ index })}
                                            sx={{ maxWidth: '100%' }}
                                        />
                                    ))
                                }
                                getOptionLabel={(option) => option.LABEL}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Ad Network"
                                        variant="outlined"
                                        sx={({ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } })}
                                    />
                                )}
                                onChange={(e, newValue) => {
                                    e.stopPropagation();
                                    const fallbackNetworks = defaultAdNetworkSelection.length > 0
                                        ? defaultAdNetworkSelection
                                        : normalizeGoogleNetworkSelection(['SEARCH']);
                                    const normalizedNetworks = normalizeGoogleNetworkSelection(
                                        newValue.length > 0 ? newValue : fallbackNetworks
                                    );
                                    setDataType(normalizedNetworks);
                                    if (adNetworkSessionKey) {
                                        sessionStorage.setItem(
                                            adNetworkSessionKey,
                                            JSON.stringify(normalizedNetworks.map((entry) => entry.CAMPAIGN_TYPE))
                                        );
                                    }
                                    if (appLoading || !reportProfile) return;
                                    setData(null);
                                    props.setLoading(true);
                                }}
                            />
                            : ''
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setFiltersOpen(false)} sx={filterButtonSx}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                const allSelected = campaignOptions.length > 0 && draftCampaignIds.length === campaignOptions.length;
                                setSelectedCampaignIds(allSelected ? null : [...draftCampaignIds]);
                                setFiltersOpen(false);
                            }}
                        >
                            Apply filters
                        </Button>
                    </DialogActions>
                </Dialog>

                <Box className="google-executive-content">
                    <TabPanel value={value} index={0}>
                        <div ref={reportRef}>
                            {Array.isArray(selectedCampaignIds) ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, pb: 1 }}>
                                    Report scope: {selectedCampaignIds.length} of {campaignOptions.length} campaigns selected
                                </Typography>
                            ) : null}
                            <GoogleOutcomeVisuals
                                data={data}
                                campaigns={campaignSummary}
                                loading={loading && !data}
                                useClicks={isVimarcMode}
                                onViewCampaigns={() => setValue(1)}
                            />
                            <Box sx={{ mt: 0 }}>
                                {!loading && trendData && Array.isArray(trendData) && trendData.length > 0 ? (
                                    <Box sx={{ ...surfaceCardSx, p: { xs: 1, sm: 1.25 } }}>
                                        <TrendChart reportStyle
                                            trendData={trendData}
                                            showCost={showCost}
                                            noteType={props.type === 'paid' ? 'google_paid' : 'google_grant'}
                                            noteView="a7edd5c0-7a77-49ed-86ad-6fe3066a0f59"
                                        />
                                    </Box>
                                ) : <Skeleton variant="rectangular" width={'100%'} height={240} sx={{ ...surfaceCardSx, mt: 0 }}></Skeleton>}
                            </Box>

                            <Box
                                sx={{
                                    mt: 2,
                                    display: 'grid',
                                    gap: 2,
                                    gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }
                                }}
                            >
                                <Card variant="outlined" sx={surfaceCardSx}>
                                    <CardHeader
                                        title={isVimarcMode ? 'Top Keywords by Clicks' : 'Top Keywords by Conversion Rate'}
                                        subheader={isVimarcMode ? 'Top 5 keyword click drivers (Impressions vs Clicks)' : 'Top 5 conversion rates (Clicks vs Conversions)'}
                                        action={(
                                            <Button size="small" onClick={() => setValue(3)}>
                                                View Keywords
                                            </Button>
                                        )}
                                    />
                                    <CardContent>
                                        {loading ? (
                                            <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 1 }} />
                                        ) : keywordFocusRows.length ? (
                                            <ReactApexChart options={ensureApexOptions(keywordFocusOptions, 'bar', theme)} series={keywordFocusSeries} type="bar" height={300} />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {isVimarcMode ? 'No keyword click data.' : 'No keyword conversion data.'}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                                <Card variant="outlined" sx={surfaceCardSx}>
                                    <CardHeader
                                        title={isVimarcMode ? 'Top Search Terms by Clicks' : 'Top Search Terms by Conversion Rate'}
                                        subheader={isVimarcMode ? 'Top 5 search click drivers (Impressions vs Clicks)' : 'Top 5 conversion rates (Clicks vs Conversions)'}
                                        action={(
                                            <Button size="small" onClick={() => setValue(4)}>
                                                View Search Terms
                                            </Button>
                                        )}
                                    />
                                    <CardContent>
                                        {loading ? (
                                            <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 1 }} />
                                        ) : searchFocusRows.length ? (
                                            <ReactApexChart options={ensureApexOptions(searchFocusOptions, 'bar', theme)} series={searchFocusSeries} type="bar" height={300} />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {isVimarcMode ? 'No search term click data.' : 'No search term conversion data.'}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Box>

                        </div>
                    </TabPanel>

                    <TabPanel value={value} index={1}>
                        <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
                            {loading ? (
                                <Skeleton variant="rectangular" width="100%" height={240} sx={{ borderRadius: 1 }} />
                            ) : (
                                <>
                                    {showCost ? (
                                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' } }}>
                                            <Card variant="outlined">
                                                <CardHeader title="Top Campaign Spend" subheader="Cost vs revenue" />
                                                <CardContent>
                                                    {campaignTopSpend.length ? (
                                                        <ReactApexChart options={ensureApexOptions(campaignSpendOptions, 'bar', theme)} series={campaignSpendSeries} type="bar" height={300} />
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">No campaign spend data.</Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                            <Card variant="outlined">
                                                <CardHeader title="Top Campaign ROAS" subheader="Highest return on ad spend" />
                                                <CardContent>
                                                    {campaignTopRoas.length ? (
                                                        <ReactApexChart options={ensureApexOptions(campaignRoasOptions, 'bar', theme)} series={campaignRoasSeries} type="bar" height={300} />
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">No ROAS data available.</Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Box>
                                    ) : null}

                                    {(campaignData ?
                                        <Box>
                                            {includeAnalysis ? (
                                                <>
                                                    <ResponsiveTabs
                                                        value={campaignTab}
                                                        onChange={(event, nextValue) => setCampaignTab(nextValue)}
                                                        aria-label="Campaigns view"
                                                        sx={{ mb: 1, ...analysisTabsSx }}
                                                    >
                                                        <Tab icon={<FaDatabase size={20} />} iconPosition="start" label="Data" sx={analysisTabSx} />
                                                        <Tab icon={<ImMagicWand size={20} />} iconPosition="start" label="Analysis" sx={analysisTabSx} />
                                                    </ResponsiveTabs>
                                                    <TabPanel value={campaignTab} index={0}>
                                                        <Box sx={{ overflowX: 'auto' }}>
                                                            <ReportTableCard branded={false}
                                                                title="Campaigns"
                                                                data={campaignData}
                                                                count={(campaignData.length > 10 ? 10 : campaignData.length)}
                                                                reportProfile={effectiveReportProfile}
                                                                themeColor={{
                                                                    primary: theme.palette.primary.main,
                                                                    secondary: theme.palette.primary.main
                                                                }}
                                                                columns={[
                                                                    {
                                                                        header: "Campaigns",
                                                                        field: "name",
                                                                        grow: true,
                                                                        minSize: 220,
                                                                        Cell: ({ cell }) => {
                                                                            return (
                                                                                <Typography textAlign={'left'} fontSize={13} fontWeight={900} >{cell.row.original.CAMPAIGN}</Typography>
                                                                            )
                                                                        },
                                                                        Footer: () => <Typography fontSize={13} fontWeight={900} textAlign={'left'}>Totals</Typography>,
                                                                    }]}
                                                            />
                                                        </Box>
                                                    </TabPanel>
                                                    <TabPanel value={campaignTab} index={1}>
                                                        {campaignAnalysisSummary.length ? (
                                                            <Box sx={{ mt: 1 }}>
                                                                <Box
                                                                    sx={{
                                                                        border: '1px solid rgba(0,0,0,0.08)',
                                                                        borderRadius: 1,
                                                                        p: 1.5,
                                                                    }}
                                                                >
                                                                    <Typography variant="subtitle2" color="primary" fontWeight={700}>
                                                                        Summary
                                                                    </Typography>
                                                                    <Divider sx={{ my: 0.75 }} />
                                                                    <Box component="ul" sx={{ pl: 3, my: 0.5 }}>
                                                                        {campaignAnalysisSummary.map((item, index) => (
                                                                            <Box key={`campaign-summary-${index}`} component="li">
                                                                                <Typography variant="body2">{item}</Typography>
                                                                            </Box>
                                                                        ))}
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        ) : null}
                                                        <StackAdaptAnalysis analysis={campaignAnalysis || {}} />
                                                    </TabPanel>
                                                </>
                                            ) : (
                                                <Box sx={{ overflowX: 'auto' }}>
                                                    <ReportTableCard branded={false}
                                                        title="Campaigns"
                                                        data={campaignData}
                                                        count={(campaignData.length > 10 ? 10 : campaignData.length)}
                                                        reportProfile={effectiveReportProfile}
                                                        themeColor={{
                                                            primary: theme.palette.primary.main,
                                                            secondary: theme.palette.primary.main
                                                        }}
                                                        columns={[
                                                            {
                                                                header: "Campaigns",
                                                                field: "name",
                                                                grow: true,
                                                                minSize: 220,
                                                                Cell: ({ cell }) => {
                                                                    return (
                                                                        <Typography textAlign={'left'} fontSize={13} fontWeight={900} >{cell.row.original.CAMPAIGN}</Typography>
                                                                    )
                                                                },
                                                                Footer: () => <Typography fontSize={13} fontWeight={900} textAlign={'left'}>Totals</Typography>,
                                                            }]}
                                                    />
                                                </Box>
                                            )}
                                        </Box>
                                        : <Skeleton variant="rectangular" width={'100%'} height={240} sx={{ borderRadius: 1 }}></Skeleton>
                                    )}
                                </>
                            )}
                        </Box>

                    </TabPanel>

                    <TabPanel value={value} index={2}>
                        {loading ? (
                            <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
                                <Skeleton variant="rectangular" width="100%" height={240} sx={{ borderRadius: 1 }} />
                                <Skeleton variant="rectangular" width="100%" height={240} sx={{ borderRadius: 1 }} />
                            </Box>
                        ) : (
                            <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
                                <Box sx={{ display: 'grid', gap: 1.5 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Top Ad Performance
                                    </Typography>
                                    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, alignItems: 'start' }}>
                                        {topAdCards.map((card) => (
                                            <Box key={card.title}>
                                                {renderTopAdCard(card.title, card.winner, card.highlight, card.chips)}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>

                                {(creativeData ?
                                    <Box sx={{ overflowX: 'auto', maxWidth: '100%', width: '100%', minWidth: 0 }}>
                                        <ReportTableCard branded={false}
                                            title="Ad Details"
                                            data={creativeData}
                                            count={(creativeData.length > 10 ? 10 : creativeData.length)}
                                            compact={true}
                                            reportProfile={effectiveReportProfile}
                                            themeColor={{
                                                primary: theme.palette.primary.main,
                                                secondary: theme.palette.primary.main
                                            }}
                                            columns={[
                                                {
                                                    header: "Campaign",
                                                    field: "campaign",
                                                    grow: true,
                                                    minSize: 200,
                                                    Cell: ({ cell }) => (
                                                        <Typography textAlign={'left'} fontSize={12} fontWeight={900} sx={{ wordBreak: 'break-word' }}>
                                                            {cell.row.original.CAMPAIGN_NAME}
                                                        </Typography>
                                                    ),
                                                },
                                                {
                                                    header: "Ad Group",
                                                    field: "adgroup",
                                                    grow: true,
                                                    maxSize: 40,
                                                    Cell: ({ cell }) => (
                                                        <Typography textAlign={'left'} fontSize={12} fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                                                            {cell.row.original.ADGROUP_NAME}
                                                        </Typography>
                                                    ),
                                                },
                                            ]}
                                            renderDetailPanel={({ row }) => (
                                                <GoogleCreativeDetails
                                                    rowData={row.original}
                                                    reportProfile={effectiveReportProfile}
                                                />
                                            )}
                                        />
                                    </Box>
                                    : <Skeleton variant="rectangular" width={'100%'} height={240} sx={{ borderRadius: 1 }}></Skeleton>
                                )}
                            </Box>
                        )}
                    </TabPanel>

                    <TabPanel value={value} index={3}>
                        <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
                            {loading ? (
                                <Skeleton variant="rectangular" width={'100%'} height={240} sx={{ borderRadius: 1 }}></Skeleton>
                            ) : (keywordData ?
                                <Box>
                                    {includeAnalysis ? (
                                        <>
                                            <ResponsiveTabs
                                                value={keywordTab}
                                                onChange={(event, nextValue) => setKeywordTab(nextValue)}
                                                aria-label="Keyword view"
                                                sx={{ mb: 1, ...analysisTabsSx }}
                                            >
                                                <Tab icon={<FaDatabase size={20} />} iconPosition="start" label="Data" sx={analysisTabSx} />
                                                <Tab icon={<ImMagicWand size={20} />} iconPosition="start" label="Analysis" sx={analysisTabSx} />
                                            </ResponsiveTabs>
                                            <TabPanel value={keywordTab} index={0}>
                                                <Box sx={{ overflowX: 'auto' }}>
                                                    <ReportTableCard branded={false}
                                                        title="Keyword"
                                                        data={keywordData}
                                                        count={(keywordData.length > 5 ? 5 : keywordData.length)}
                                                        reportProfile={effectiveReportProfile}
                                                        themeColor={{
                                                            primary: theme.palette.primary.main,
                                                            secondary: theme.palette.primary.main
                                                        }}
                                                        options={{
                                                            rowStyle: { padding: 0, margin: 0 },
                                                            cellStyle: { border: '1px solid ' + lightenHexColor(theme.palette.primary.main, 0), textAlign: 'center', padding: theme.spacing(.5), margin: 0, fontSize: '13px' },
                                                            headerStyle: { textAlign: "center", backgroundColor: lightenHexColor(theme.palette.primary.main, 0), color: "#fff", border: '1px solid ' + theme.palette.primary.main, paddingTop: 5, paddingBottom: 5 },
                                                            grouping: false,
                                                            exportButton: true,
                                                            exportAllData: true,
                                                            search: true,
                                                            searchFieldVariant: "outlined",
                                                            paging: (keywordData.length > 5 ? true : false),
                                                            exportFileName: moment(new Date()).format("YYYYMMDD.hhmmss"),
                                                            pageSizeOptions: [10, 25, 50, 100],
                                                            showTitle: false,
                                                            toolbar: true,
                                                            sort: true
                                                        }}
                                                        columns={[
                                                            {
                                                                header: "Keywords",
                                                                field: "name",
                                                                grow: true,
                                                                minSize: 200,
                                                                Cell: ({ cell }) => {
                                                                    return (
                                                                        <Typography textAlign={'left'} fontSize={13} fontWeight={900} >{cell.row.original.KEYWORD}</Typography>
                                                                    )
                                                                },
                                                                Footer: () => <Typography fontSize={13} fontWeight={900} textAlign={'left'}>Totals</Typography>,
                                                            }]}
                                                    />
                                                </Box>
                                            </TabPanel>
                                            <TabPanel value={keywordTab} index={1}>
                                                {keywordAnalysisSummary.length ? (
                                                    <Box sx={{ mt: 1 }}>
                                                        <Box
                                                            sx={{
                                                                border: '1px solid rgba(0,0,0,0.08)',
                                                                borderRadius: 1,
                                                                p: 1.5,
                                                            }}
                                                        >
                                                            <Typography variant="subtitle2" color="primary" fontWeight={700}>
                                                                Summary
                                                            </Typography>
                                                            <Divider sx={{ my: 0.75 }} />
                                                            <Box component="ul" sx={{ pl: 3, my: 0.5 }}>
                                                                {keywordAnalysisSummary.map((item, index) => (
                                                                    <Box key={`keyword-summary-${index}`} component="li">
                                                                        <Typography variant="body2">{item}</Typography>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                ) : null}
                                                <StackAdaptAnalysis analysis={keywordAnalysis || {}} />
                                            </TabPanel>
                                        </>
                                    ) : (
                                        <Box sx={{ overflowX: 'auto' }}>
                                                <ReportTableCard branded={false}
                                                    title="Keyword"
                                                    data={keywordData}
                                                    count={(keywordData.length > 5 ? 5 : keywordData.length)}
                                                    reportProfile={effectiveReportProfile}
                                                    themeColor={{
                                                        primary: theme.palette.primary.main,
                                                        secondary: theme.palette.primary.main
                                                    }}
                                                    options={{
                                                        rowStyle: { padding: 0, margin: 0 },
                                                        cellStyle: { border: '1px solid ' + lightenHexColor(theme.palette.primary.main, 0), textAlign: 'center', padding: theme.spacing(.5), margin: 0, fontSize: '13px' },
                                                        headerStyle: { textAlign: "center", backgroundColor: lightenHexColor(theme.palette.primary.main, 0), color: "#fff", border: '1px solid ' + theme.palette.primary.main, paddingTop: 5, paddingBottom: 5 },
                                                        grouping: false,
                                                        exportButton: true,
                                                        exportAllData: true,
                                                        search: true,
                                                        searchFieldVariant: "outlined",
                                                        paging: (keywordData.length > 5 ? true : false),
                                                        exportFileName: moment(new Date()).format("YYYYMMDD.hhmmss"),
                                                        pageSizeOptions: [10, 25, 50, 100],
                                                        showTitle: false,
                                                        toolbar: true,
                                                        sort: true
                                                    }}
                                                    columns={[
                                                        {
                                                            header: "Keywords",
                                                            field: "name",
                                                            grow: true,
                                                            minSize: 200,
                                                            Cell: ({ cell }) => {
                                                                return (
                                                                    <Typography textAlign={'left'} fontSize={13} fontWeight={900} >{cell.row.original.KEYWORD}</Typography>
                                                                )
                                                            },
                                                            Footer: () => <Typography fontSize={13} fontWeight={900} textAlign={'left'}>Totals</Typography>,
                                                        }]}
                                                />
                                        </Box>
                                    )}
                                </Box>
                                : <Skeleton variant="rectangular" width={'100%'} height={240} sx={{ borderRadius: 1 }}></Skeleton>
                            )}
                        </Box>
                    </TabPanel>

                    <TabPanel value={value} index={4}>
                        <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
                            {loading ? (
                                <Skeleton variant="rectangular" width={'100%'} height={240} sx={{ borderRadius: 1 }}></Skeleton>
                            ) : (searchData ?
                                <Box>
                                    {includeAnalysis ? (
                                        <>
                                            <ResponsiveTabs
                                                value={searchTab}
                                                onChange={(event, nextValue) => setSearchTab(nextValue)}
                                                aria-label="Search view"
                                                sx={{ mb: 1, ...analysisTabsSx }}
                                            >
                                                <Tab icon={<FaDatabase size={20} />} iconPosition="start" label="Data" sx={analysisTabSx} />
                                                <Tab icon={<ImMagicWand size={20} />} iconPosition="start" label="Analysis" sx={analysisTabSx} />
                                            </ResponsiveTabs>
                                            <TabPanel value={searchTab} index={0}>
                                                <Box sx={{ display: 'grid', gap: 1.25 }}>
                                                    {hasSearchCampaignBreakout ? (
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                            <TextField
                                                                select
                                                                size="small"
                                                                label="Campaign"
                                                                value={searchCampaignFilter}
                                                                onChange={(event) => setSearchCampaignFilter(event.target.value)}
                                                                sx={{ minWidth: { xs: '100%', sm: 260 }, ...({ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }) }}
                                                            >
                                                                {searchCampaignOptions.map((option) => (
                                                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                                                ))}
                                                            </TextField>
                                                        </Box>
                                                    ) : null}
                                                    <Box sx={{ overflowX: 'auto' }}>
                                                    <ReportTableCard branded={false}
                                                        title="Search Terms"
                                                        data={filteredSearchData}
                                                        count={(filteredSearchData.length > 10 ? 10 : filteredSearchData.length)}
                                                        reportProfile={effectiveReportProfile}
                                                        themeColor={{
                                                            primary: theme.palette.primary.main,
                                                            secondary: theme.palette.primary.main
                                                        }}
                                                        columns={[
                                                            ...(hasSearchCampaignBreakout ? [{
                                                                header: "Campaign",
                                                                field: "campaign",
                                                                grow: true,
                                                                minSize: 220,
                                                                Cell: ({ cell }) => (
                                                                    <Typography textAlign={'left'} fontSize={13} fontWeight={700}>
                                                                        {getGoogleCampaignName(cell.row.original)}
                                                                    </Typography>
                                                                ),
                                                            }] : []),
                                                            {
                                                                header: "Search Term",
                                                                field: "search",
                                                                grow: true,
                                                                minSize: 220,
                                                                Cell: ({ cell }) => {
                                                                    return (
                                                                        <Typography textAlign={'left'} fontSize={13} fontWeight={900} >{cell.row.original.SEARCH}</Typography>
                                                                    )
                                                                },
                                                                Footer: () => <Typography fontSize={13} fontWeight={900} textAlign={'left'}>Totals</Typography>,
                                                            }]}
                                                    />
                                                    </Box>
                                                </Box>
                                            </TabPanel>
                                            <TabPanel value={searchTab} index={1}>
                                                {searchAnalysisSummary.length ? (
                                                    <Box sx={{ mt: 1 }}>
                                                        <Box
                                                            sx={{
                                                                border: '1px solid rgba(0,0,0,0.08)',
                                                                borderRadius: 1,
                                                                p: 1.5,
                                                            }}
                                                        >
                                                            <Typography variant="subtitle2" color="primary" fontWeight={700}>
                                                                Summary
                                                            </Typography>
                                                            <Divider sx={{ my: 0.75 }} />
                                                            <Box component="ul" sx={{ pl: 3, my: 0.5 }}>
                                                                {searchAnalysisSummary.map((item, index) => (
                                                                    <Box key={`search-summary-${index}`} component="li">
                                                                        <Typography variant="body2">{item}</Typography>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                ) : null}
                                                <StackAdaptAnalysis analysis={searchAnalysis || {}} />
                                            </TabPanel>
                                        </>
                                    ) : (
                                        <Box sx={{ display: 'grid', gap: 1.25 }}>
                                            {hasSearchCampaignBreakout ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <TextField
                                                        select
                                                        size="small"
                                                        label="Campaign"
                                                        value={searchCampaignFilter}
                                                        onChange={(event) => setSearchCampaignFilter(event.target.value)}
                                                        sx={{ minWidth: { xs: '100%', sm: 260 }, ...({ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }) }}
                                                    >
                                                        {searchCampaignOptions.map((option) => (
                                                            <MenuItem key={option} value={option}>{option}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Box>
                                            ) : null}
                                            <Box sx={{ overflowX: 'auto' }}>
                                            <ReportTableCard branded={false}
                                                title="Search Terms"
                                                data={filteredSearchData}
                                                count={(filteredSearchData.length > 10 ? 10 : filteredSearchData.length)}
                                                reportProfile={effectiveReportProfile}
                                                themeColor={{
                                                    primary: theme.palette.primary.main,
                                                    secondary: theme.palette.primary.main
                                                }}
                                                columns={[
                                                    ...(hasSearchCampaignBreakout ? [{
                                                        header: "Campaign",
                                                        field: "campaign",
                                                        grow: true,
                                                        minSize: 220,
                                                        Cell: ({ cell }) => (
                                                            <Typography textAlign={'left'} fontSize={13} fontWeight={700}>
                                                                {getGoogleCampaignName(cell.row.original)}
                                                            </Typography>
                                                        ),
                                                    }] : []),
                                                    {
                                                        header: "Search Term",
                                                        field: "search",
                                                        grow: true,
                                                        minSize: 220,
                                                        Cell: ({ cell }) => {
                                                            return (
                                                                <Typography textAlign={'left'} fontSize={13} fontWeight={900} >{cell.row.original.SEARCH}</Typography>
                                                            )
                                                        },
                                                        Footer: () => <Typography fontSize={13} fontWeight={900} textAlign={'left'}>Totals</Typography>,
                                                    }]}
                                            />
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                                : <Skeleton variant="rectangular" width={'100%'} height={240} sx={{ borderRadius: 1 }}></Skeleton>
                            )}
                        </Box>
                    </TabPanel>

                    <TabPanel value={value} index={5}>
                        <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2,
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                                    '& > .MuiCard-root': plainCardSx,
                                }}
                            >
                                <Card variant="outlined">
                                    <CardHeader title="Geo Coverage" />
                                    <CardContent>
                                        <Typography variant="h4" fontWeight={800}>{formatNumber(filteredLocationSummary.length)}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {locationLevelFilter === ALL_LOCATION_LEVELS
                                                ? 'Locations returned from Google Ads geo targeting.'
                                                : `${locationLevelFilter} rows returned from Google Ads geo targeting.`}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">{locationConversionLabel}</Typography>
                                    </CardContent>
                                </Card>
                                <Card variant="outlined">
                                    <CardHeader title="Highest Traffic Location" />
                                    <CardContent>
                                        <Typography variant="h6" fontWeight={800}>{topLocationByTraffic?.label || 'No location data'}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {topLocationByTraffic
                                                ? `${formatNumber(topLocationByTraffic.clicks)} clicks from ${formatNumber(topLocationByTraffic.impressions)} impressions.`
                                                : 'Traffic volume is not available for this range.'}
                                        </Typography>
                                    </CardContent>
                                </Card>
                                <Card variant="outlined">
                                    <CardHeader title="Top Location by Revenue" />
                                    <CardContent>
                                        <Typography variant="h6" fontWeight={800}>{locationTopRevenue[0]?.label || 'No location data'}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {locationTopRevenue[0]
                                                ? `${formatCurrency(locationTopRevenue[0].revenue)} revenue from ${formatNumber(locationTopRevenue[0].conversions)} conversions.`
                                                : 'Google Ads did not return location data for this range.'}
                                        </Typography>
                                    </CardContent>
                                </Card>
                                <Card variant="outlined">
                                    <CardHeader title={showCost ? 'Best ROAS Location' : 'Best Conversion Rate'} />
                                    <CardContent>
                                        <Typography variant="h6" fontWeight={800}>
                                            {showCost ? (bestLocationByRoas?.label || 'No location data') : (bestLocationByConvRate?.label || 'No location data')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {showCost
                                                ? (
                                                    bestLocationByRoas
                                                        ? `${bestLocationByRoas.roas.toFixed(2)}x ROAS in the selected range.`
                                                        : 'ROAS requires spend and revenue in the geo view.'
                                                )
                                                : (
                                                    bestLocationByConvRate
                                                        ? `${formatPercent(bestLocationByConvRate.convRate)} conversion rate from ${formatNumber(bestLocationByConvRate.clicks)} clicks.`
                                                        : 'Conversion rate requires clicks in the selected range.'
                                                )}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Box>

                            <Card variant="outlined" sx={plainCardSx}>
                                <CardHeader title="Interactive Geographic Performance" subheader="Select a country, then a U.S. state, to explore local Google Ads performance." />
                                <CardContent><LocationMap rows={filteredLocationSummary} loading={loading} /></CardContent>
                            </Card>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2,
                                    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.4fr) minmax(320px, 0.8fr)' },
                                    alignItems: 'stretch',
                                    '& > .MuiCard-root': plainCardSx,
                                }}
                            >
                                <Card variant="outlined">
                                    <CardHeader
                                        title="Top Locations by Traffic"
                                        subheader="Ranked by clicks to surface the strongest geo demand pockets."
                                    />
                                    <CardContent>
                                        {locationTrafficChartRows.length ? (
                                            <ReactApexChart
                                                options={ensureApexOptions(locationTrafficOptions, 'bar', theme)}
                                                series={locationTrafficSeries}
                                                type="bar"
                                                height={320}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Google Ads did not return enough location traffic for a ranked view.
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>

                                <Card variant="outlined">
                                    <CardHeader
                                        title="Location Level Mix"
                                        subheader="How much of this geo dataset is city, postal, state, and other rollups."
                                    />
                                    <CardContent sx={{ display: 'grid', gap: 2 }}>
                                        {locationLevelMixSeries.length ? (
                                            <ReactApexChart
                                                options={ensureApexOptions(locationLevelMixOptions, 'donut', theme)}
                                                series={locationLevelMixSeries}
                                                type="donut"
                                                height={280}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                No location level metadata is available for this date range.
                                            </Typography>
                                        )}
                                        <Box sx={{ display: 'grid', gap: 1 }}>
                                            {locationLevelBreakdown.slice(0, 4).map((row) => (
                                                <Box
                                                    key={row.level}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: 2,
                                                        py: 1,
                                                        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography fontSize={13} fontWeight={800}>{row.level}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatNumber(row.locations)} locations • {formatNumber(row.clicks)} clicks
                                                        </Typography>
                                                    </Box>
                                                    <Typography fontSize={13} fontWeight={700}>
                                                        {formatNumber(row.conversions)} conv
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2,
                                    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.4fr) minmax(320px, 0.8fr)' },
                                    alignItems: 'stretch',
                                    '& > .MuiCard-root': plainCardSx,
                                }}
                            >
                                <Card variant="outlined">
                                    <CardHeader
                                        title={hasLocationRevenue ? 'Top Locations by Revenue' : 'Top Locations by Conversions'}
                                        subheader={hasLocationRevenue
                                            ? 'Revenue concentration by geo target.'
                                            : 'Highest-converting locations when revenue is unavailable.'}
                                    />
                                    <CardContent>
                                        {locationValueChartRows.length ? (
                                            <ReactApexChart
                                                options={ensureApexOptions(locationValueOptions, 'bar', theme)}
                                                series={locationValueSeries}
                                                type="bar"
                                                height={320}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Google Ads did not return enough conversion value data for a ranked view.
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>

                                <Card variant="outlined">
                                    <CardHeader
                                        title="Efficiency Standouts"
                                        subheader="Quick read on the strongest response rates in the selected geo slice."
                                    />
                                    <CardContent sx={{ display: 'grid', gap: 1.5 }}>
                                        <Box sx={{ p: 1.5, borderRadius: 2, background: alpha(theme.palette.primary.main, 0.05) }}>
                                            <Typography variant="overline" sx={{ letterSpacing: '0.12em' }}>Highest CTR</Typography>
                                            <Typography variant="h6" fontWeight={800}>{bestLocationByCtr?.label || 'No location data'}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {bestLocationByCtr
                                                    ? `${formatPercent(bestLocationByCtr.ctr)} CTR from ${formatNumber(bestLocationByCtr.impressions)} impressions.`
                                                    : 'CTR requires impression data.'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ p: 1.5, borderRadius: 2, background: alpha(theme.palette.secondary.main, 0.06) }}>
                                            <Typography variant="overline" sx={{ letterSpacing: '0.12em' }}>Best Conversion Rate</Typography>
                                            <Typography variant="h6" fontWeight={800}>{bestLocationByConvRate?.label || 'No location data'}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {bestLocationByConvRate
                                                    ? `${formatPercent(bestLocationByConvRate.convRate)} conversion rate from ${formatNumber(bestLocationByConvRate.conversions)} conversions.`
                                                    : 'Conversion rate requires click data.'}
                                            </Typography>
                                        </Box>
                                        {showCost ? (
                                            <Box sx={{ p: 1.5, borderRadius: 2, background: alpha(theme.palette.success.main, 0.08) }}>
                                                <Typography variant="overline" sx={{ letterSpacing: '0.12em' }}>Best ROAS</Typography>
                                                <Typography variant="h6" fontWeight={800}>{bestLocationByRoas?.label || 'No location data'}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {bestLocationByRoas
                                                        ? `${bestLocationByRoas.roas.toFixed(2)}x ROAS with ${formatCurrency(bestLocationByRoas.revenue)} in revenue.`
                                                        : 'ROAS requires both cost and revenue in the geo rows.'}
                                                </Typography>
                                            </Box>
                                        ) : null}
                                    </CardContent>
                                </Card>
                            </Box>

                            <Box sx={{ overflowX: 'auto' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.25 }}>
                                    <TextField
                                        select
                                        size="small"
                                        label="Location Level"
                                        value={locationLevelFilter}
                                        onChange={(event) => setLocationLevelFilter(event.target.value)}
                                        sx={{ minWidth: { xs: '100%', sm: 220 }, ...({ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }) }}
                                    >
                                        {locationLevelOptions.map((option) => (
                                            <MenuItem key={option} value={option}>{option}</MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                                <TableCard branded={false}
                                    title="Locations"
                                    data={filteredLocationSummary}
                                    appendDefaultMetrics={false}
                                    count={(filteredLocationSummary.length > 10 ? 10 : filteredLocationSummary.length)}
                                    reportProfile={reportProfile}
                                    service={props.type}
                                    themeColor={{
                                        primary: theme.palette.primary.main,
                                        secondary: theme.palette.primary.main
                                    }}
                                    columns={[
                                        {
                                            header: "Location",
                                            field: "location",
                                            grow: true,
                                            minSize: 220,
                                            Cell: ({ cell }) => (
                                                <Typography textAlign={'left'} fontSize={13} fontWeight={900}>
                                                    {cell.row.original.label || cell.row.original.LOCATION || cell.row.original.NAME || 'Unknown'}
                                                </Typography>
                                            ),
                                            Footer: () => <Typography fontSize={13} fontWeight={900} textAlign={'left'}>Totals</Typography>,
                                        },
                                        {
                                            header: 'Impressions',
                                            accessorKey: 'impressions',
                                            minSize: 120,
                                            Cell: ({ cell }) => <Typography fontSize={12} fontWeight={600}>{formatNumber(cell.getValue())}</Typography>,
                                            Footer: () => <Typography fontSize={12} fontWeight={700}>{formatNumber(filteredLocationSummary.reduce((sum, row) => sum + row.impressions, 0))}</Typography>,
                                        },
                                        {
                                            header: 'Clicks',
                                            accessorKey: 'clicks',
                                            minSize: 110,
                                            Cell: ({ cell }) => <Typography fontSize={12} fontWeight={600}>{formatNumber(cell.getValue())}</Typography>,
                                            Footer: () => <Typography fontSize={12} fontWeight={700}>{formatNumber(filteredLocationSummary.reduce((sum, row) => sum + row.clicks, 0))}</Typography>,
                                        },
                                        {
                                            header: 'CTR',
                                            accessorKey: 'ctr',
                                            minSize: 100,
                                            Cell: ({ cell }) => <Typography fontSize={12} fontWeight={600}>{formatPercent(cell.getValue())}</Typography>,
                                            Footer: () => {
                                                const impressionsTotal = filteredLocationSummary.reduce((sum, row) => sum + row.impressions, 0);
                                                const clicksTotal = filteredLocationSummary.reduce((sum, row) => sum + row.clicks, 0);
                                                const ctrTotal = impressionsTotal > 0 ? (clicksTotal / impressionsTotal) * 100 : 0;
                                                return <Typography fontSize={12} fontWeight={700}>{formatPercent(ctrTotal)}</Typography>;
                                            },
                                        },
                                        {
                                            header: 'Conversions',
                                            accessorKey: 'conversions',
                                            minSize: 120,
                                            Cell: ({ cell }) => <Typography fontSize={12} fontWeight={600}>{formatNumber(cell.getValue())}</Typography>,
                                            Footer: () => <Typography fontSize={12} fontWeight={700}>{formatNumber(filteredLocationSummary.reduce((sum, row) => sum + row.conversions, 0))}</Typography>,
                                        },
                                        {
                                            header: 'Conv %',
                                            accessorKey: 'convRate',
                                            minSize: 100,
                                            Cell: ({ cell }) => <Typography fontSize={12} fontWeight={600}>{formatPercent(cell.getValue())}</Typography>,
                                            Footer: () => {
                                                const clicksTotal = filteredLocationSummary.reduce((sum, row) => sum + row.clicks, 0);
                                                const conversionTotal = filteredLocationSummary.reduce((sum, row) => sum + row.conversions, 0);
                                                const convRateTotal = clicksTotal > 0 ? (conversionTotal / clicksTotal) * 100 : 0;
                                                return <Typography fontSize={12} fontWeight={700}>{formatPercent(convRateTotal)}</Typography>;
                                            },
                                        },
                                        {
                                            header: 'Revenue',
                                            accessorKey: 'revenue',
                                            minSize: 130,
                                            Cell: ({ cell }) => <Typography fontSize={12} fontWeight={600}>{formatCurrency(cell.getValue())}</Typography>,
                                            Footer: () => <Typography fontSize={12} fontWeight={700}>{formatCurrency(filteredLocationSummary.reduce((sum, row) => sum + row.revenue, 0))}</Typography>,
                                        },
                                        ...(showCost ? [{
                                            header: 'ROAS',
                                            accessorKey: 'roas',
                                            minSize: 100,
                                            Cell: ({ cell }) => <Typography fontSize={12} fontWeight={600}>{`${Number(cell.getValue() || 0).toFixed(2)}x`}</Typography>,
                                            Footer: () => {
                                                const costTotal = filteredLocationSummary.reduce((sum, row) => sum + row.cost, 0);
                                                const revenueTotal = filteredLocationSummary.reduce((sum, row) => sum + row.revenue, 0);
                                                const roasTotal = costTotal > 0 ? revenueTotal / costTotal : 0;
                                                return <Typography fontSize={12} fontWeight={700}>{`${roasTotal.toFixed(2)}x`}</Typography>;
                                            },
                                        }] : [])
                                    ]}
                                />
                            </Box>
                        </Box>
                    </TabPanel>

                    <TabPanel value={value} index={6}>
                        <div>
                            <Comments
                                eDate={sessionStorage.getItem('sDate')}
                                sDate={sessionStorage.getItem('eDate')}
                                color={theme.palette.primary.main}
                                admintoken={admintoken}
                                view={"a7edd5c0-7a77-49ed-86ad-6fe3066a0f59"}
                                type={(props.type === "paid" ? "google_paid" : "google_grant")}
                                setLoading={props.setLoading}
                            ></Comments>
                        </div>
                    </TabPanel>
                </Box>



            </Box >
            <ReportQuestionAssistant
                reportType={props.type === 'grant' ? 'google_grant' : 'google_paid'}
                reportTitle={props.type === 'grant' ? 'Google Grant' : 'Google Paid'}
                profile={reportProfile}
                contextPayload={googleAssistantConfig}
            />
        </Box ></ThemeProvider>
    );
}
